import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import { Link } from 'react-router-dom';
import * as _ from 'lodash';
import NotesPopup from './NotesPopup';
import EditNoteModal from './EditNotes';
import RunsheetEditJobPopup from './RunSheetEditJob';
import RunsheetInvoiceItemsPopup from './RunSheetInvoiceItemsPopup';
import RunsheetLinkedJobPopup from './RunSheetLinkedJobs';
import PhotoViewerModal from './PhotoViewer';
import RunsheetAddDetailModalData from './RunSheetAddDetail';
import EditSiteTimesModal from './RunsheetEditSiteTimes';
import authService from './api-authorization/AuthorizeService';

interface RunSheetUpdate {
    scheduleDate: string;
    systemCompanyId: number;
    canEdit: boolean;
    runsheetChecked: boolean;
    runsheetCheckedChanged: boolean;
    canUpdateChecked: boolean;
    allocatedJobs: AllocatedJob[];
    filteredJobs: AllocatedJob[];
    allocatedSimpleJobs: AllocateSimpleJob[];
    unallocatedJobs: number;
    filteredSimpleJobs: AllocateSimpleJob[];
    branchFilterId: number;
    scheduleBranchOptions: GenericList[];
    truckFilterId: number;
    truckTeam: string;
    truckOptions: TruckListOption[];
    showNotes: boolean;
    showSimpleNotes: boolean;
    showNoteType: number;   //1=Driver Notes, 2=Schedule Notes, 3=Simple Schedule Notes
    editNotes: EditNotes;
    gst: number;
    itemTypes: ItemTypeList[];
    items: ItemList[];
    invoiceItem: InvoiceItem;
    showInvoiceItemModal: boolean;
    invoiceItemModalType: number;       //1: Extra Invoice Items Scaffold, 2: Callout Scaffold, 3: Callout Dunny, 4: Extra Invoice Items Dunny
    showEditJobModal: boolean;
    fromLinkedJob: boolean;
    invoiceItemParentIndex: number;
    showLinkedJobs: boolean;
    linkedJobs: AllocatedJob[];
    showPhotos: boolean;
    photoIndex: number;
    photos: AppFile[];
    showAddUnitNumber: boolean;
    scheduleId: number;
    showOnSiteMins: boolean;
    editOnSiteMins: EditOnSiteMinutes;
    jobIndex: number;
    itemIndex: number;
    saveDisabled: boolean;
    updateAll: boolean;
    loading: boolean;
    errors: any;
}

interface RunSheetProps {
    saveReschedule(data: any): void;
}

interface AppFile {
    id: number;
    fileType: string;
    fileName: string;
    content: any;
}

interface AllocatedJob {
    id: number; //runsheet Id
    scheduleId: number;
    jobId: number;
    jobNumber: number;
    itemIndex: number;
    truckId: number;
    truck: string;
    unitNumber: string;
    siteAddress: string;
    departureTime: string;
    siteArrivalTime: string;
    downTime: string;
    siteCompletionTime: string;
    departureTimeFormatted: string;
    siteArrivalTimeFormatted: string;
    siteCompletionTimeFormatted: string;
    onSiteMins: number;
    estTimeMins: number;
    estTime: string;
    callout: boolean;
    calloutComplete: boolean;
    driverNotes: string;
    items: AllocatedJobItem[];
    extraInvoiceItems: ExtraInvoiceItem[];
    isDirty: boolean;
    isReschedule: boolean;
    hasPhotos: boolean;
    error: string;
}

interface AllocatedJobItem {
    id: number;
    companyId: number;
    itemIndex: number;
    unitNumber: string;
    typeId: number;
    type: string;
    manualItem: boolean;
    description: string;
    itemId: number;
    item: string;
    itemTypeId: number;
    hirePeriodDays: number;
    isEws: boolean;
    isEvent: boolean;
    invoiceItem: boolean;
    unitOfMeasure: string;
    scheduleKwikstageId: number;
    quantityTotal: number;
    quantityScheduled: number;
    quantity: number;
    quantityLost: number;
    quantityType: string;
    reschedule: boolean;
    rescheduleComplete: boolean;
    price: number;
    initial: number;
    travel: number;
    cleaningFee: number;
    overhireRate: number;
    calloutItem: boolean;
    scheduleNotes: string;
    itemIsKwikstage: boolean;
    kwikstageItems: AllocateJobKwikstageItem[];
    truckAssistScheduleId: number;
    isCustomerPickup: boolean;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface AllocateJobKwikstageItem {
    scheduleKwikstageId: number;
    scheduleId: number;
    starterBayId: number;
    starterBay: string;
    liftId: number;
    lift: string;
    bays: number;
    baysComplete: number;
    baysLost: number;
    isCustomerPickup: boolean;
    reschedule: boolean;
}

interface AllocateSimpleJob {
    scheduleId: number;
    jobId: number;
    companyId: number;
    jobNumber: number;
    itemIndex: number;
    truckId: number;
    truck: string;
    unitNumber: string;
    customer: string;
    siteAddress: string;
    typeId: number;
    type: string;
    hirePeriodDays: number;
    defaultHirePeriod: number;
    customHirePeriod: boolean;
    size: number;
    sizeOriginal: number;   //used to reset size if reschedule ticked
    extraHours: number;
    extraHoursOriginal: number;     //used to reset extra hours if reschedule ticked
    confinedSpace: boolean;
    manualItem: boolean;
    isPlaceholder: boolean;
    description: string;
    gateCode: string;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemTypeId: number;
    itemTypeGroupId: number;    //so I can get Fencing Manual jobs
    productGroupId: number;
    frequencyId: number;
    locationId: number;
    showUnitNumber: boolean;
    itemUnitNumber: string;
    unitNumberRequired: boolean;
    goAhead: boolean;
    isEws: boolean;
    isEvent: boolean;
    quantityTotal: number;
    quantityScheduled: number;
    quantity: number;
    quantityLost: number;
    fencePanels: number;
    fencePanelsDamaged: number;
    fenceBlocks: number;
    fenceClamps: number;
    fencePickets: number;
    fenceSigns: number;
    fenceBraces: number;
    fencePanelsExtra: number;
    fenceBlocksExtra: number;
    fenceClampsExtra: number;
    fencePicketsExtra: number;
    fenceSignsExtra: number;
    fenceBracesExtra: number;
    hasBraceItem: boolean;  //if there is a separate brace item we don't need to get # braces
    waterBoardNotified: boolean;
    epaTransportCertificateCheckRequired: boolean;  //only some customers require this for EPA jobs
    epaTransportCertificateChecked: boolean;
    invoice: boolean;
    reschedule: boolean;
    onHold: boolean;
    initial: number;
    travel: number;
    cleaningFee: number;
    trenchFee: number;
    price: number;
    overhireRate: number;
    callout: boolean;
    calloutItem: boolean;
    scheduleNotes: string;
    extraInvoiceItems: ExtraInvoiceItem[];
    isDirty: boolean;
    isReschedule: boolean;
    isCustomerPickup: boolean;
    truckAssistScheduleId: number;
    trolley: boolean;
    extras: NewJobQuoteItemExtra;
    canEdit: boolean;
    canEditJob: boolean;
    canEditOverhire: boolean;
    rowVersionTimestamp: Uint8Array;
}

interface TruckListOption {
    id: number,
    name: string,
    registrationNumber: string
    scheduleBranchId: number;
}

interface EditNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

interface EditOnSiteMinutes {
    jobId: number;
    runsheetId: number;
    onSiteMinutes: number;
}

interface NewJobQuoteItemExtra {
    itemId: number;
    quantity: number;
    equipped: boolean;
}

interface InvoiceItem {
    noCalloutInvoiceNotes: string;
    calloutNotes: string;
    invoiceItems: InvoiceItems[];
}

interface InvoiceItems {
    rowIndex: number;
    id: number;
    itemId: number;
    itemTypeId: number;
    scheduleId: number;
    jobQuoteItemId: number;
    rate: number;
    gst: number;
    amount: number;
    description: string;
    active: boolean;
}

interface ExtraInvoiceItem {
    itemTypeId: number;
    amount: number;
    description: string;
}

interface ItemTypeList {
    id: number;
    name: string;
    chargeGst: boolean;
}

interface ItemList {
    id: number;
    jobQuoteItemId: number;
    name: string;
    chargeGst: boolean;
}

interface GenericList {
    id: number;
    name: string;
}

interface ScheduleNotes {
    Id: number;
    Notes: string;
    ItemId: number;
    Item: string;
    SubId: any;
}

interface RunsheetCalloutItem {
    scheduleDate: string;
    job: AllocatedJob,
    reasonForCallout: string;
    reasonNoCalloutInvoice: string;
    subId: any;
}  

interface SimpleValidation {
    errorMessage: string;
    error: boolean;
}

const win: any = window;
const Stimulsoft = win.Stimulsoft;
let timeout;

export default class RunSheetDetails extends React.Component<RunSheetProps, RunSheetUpdate> {
    constructor(props: any) {
        super(props);

        //set tomorrow as the default schedule date
        var scheduleDate = DateHelpers.getCurrentDate();

        this.state = {
            scheduleDate: scheduleDate,
            systemCompanyId: 2,
            canEdit: false,
            runsheetChecked: false,
            runsheetCheckedChanged: false,
            canUpdateChecked: true,
            allocatedJobs: [],
            filteredJobs: [],
            allocatedSimpleJobs: [],
	        unallocatedJobs: 0,
            filteredSimpleJobs: [],
            branchFilterId: -1,
            scheduleBranchOptions: [],
            truckFilterId: -1,
            truckTeam: "",
            truckOptions: [],
            showNotes: false,
            showSimpleNotes: false,
            showNoteType: 0,
            editNotes: {
                heading: "",
                parentIndex: 0,
                rowIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            },
            gst: 0,
            items: [],
            itemTypes: [],
            invoiceItem: {
                invoiceItems: [],
                calloutNotes: "",
                noCalloutInvoiceNotes: ""
            },
            invoiceItemParentIndex: 0,
            showEditJobModal: false,
            fromLinkedJob: false,
            showInvoiceItemModal: false,
            invoiceItemModalType: 0,
            showLinkedJobs: false,
            linkedJobs: [],
            showPhotos: false,
            photoIndex: 0,
            photos: [],
            showAddUnitNumber: false,
            scheduleId: -1,
            showOnSiteMins: false,
            editOnSiteMins: {
                jobId: -1,
                runsheetId: -1,
                onSiteMinutes: 0
            },
            jobIndex: -1,
            itemIndex: -1,
            updateAll: false,
            saveDisabled: false,
            errors: {},
            loading: true
        };
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //get item types
        axios.get('api/Invoicing/GetInvoiceItemTypes?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ itemTypes: res.data.itemTypes, gst: res.data.gst, branchFilterId: -1 });

            axios.get('api/RunSheet/GetRunsheetBranches?SubId=' + user.sub, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                var branchFilterId = res.data.defaultScheduleBranchId;    //set the default branch
                var branches = res.data.branches;
                var systemCompanyId = res.data.systemCompanyId;
                this.setState({
                    scheduleBranchOptions: branches,
                    branchFilterId: branchFilterId,
                    systemCompanyId: systemCompanyId,
                    canEdit: res.data.canEdit
                });
                if (systemCompanyId === 2) {
                    this.getRunsheetData(branchFilterId);
                } else {
                    this.getSimpleRunsheetData(branchFilterId);
                }
                
            })
            .catch(error => {
                toast.error(error.message);
            });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async getSimpleRunsheetData(scheduleBranchId: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/RunSheet/GetRunsheetSimpleData?ScheduleDate=' + this.state.scheduleDate + '&ScheduleBranchId=' + scheduleBranchId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var allocatedJobs = res.data.allocatedItems;
            for (var i = 0; i < allocatedJobs.length; i++) {
                allocatedJobs[i].itemIndex = i;
                allocatedJobs[i].isDirty = false;
                allocatedJobs[i].price = allocatedJobs[i].price.toFixed(2);
                allocatedJobs[i].overhireRate = allocatedJobs[i].overhireRate.toFixed(2);
                allocatedJobs[i].extraInvoiceItems = [];
                
            }
            var filteredTrucks: TruckListOption[] = res.data.truckItems;
            filteredTrucks = filteredTrucks.filter(x => x.scheduleBranchId == scheduleBranchId);
            this.setState({
                runsheetChecked: true,
                canUpdateChecked: false,
                saveDisabled: false,
                truckOptions: res.data.truckItems,
                truckFilterId: -1,
                truckTeam: "",
                allocatedSimpleJobs: allocatedJobs,
                filteredJobs: [],
                unallocatedJobs: res.data.unallocatedJobs,
                errors: {},
                loading: false
            })
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    async getRunsheetData(scheduleBranchId: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/RunSheet/GetRunsheetData?ScheduleDate=' + this.state.scheduleDate + '&ScheduleBranchId=' + scheduleBranchId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var allocatedJobs = res.data.allocatedItems;
            for (var i = 0; i < allocatedJobs.length; i++) {
                allocatedJobs[i].itemIndex = i;
                let allocatedJobItem = allocatedJobs[i].items;
                for (var y = 0; y < allocatedJobItem.length; y++) {
                    allocatedJobItem[y].itemIndex = y;
                    allocatedJobItem[y].isDirty = false;
                    allocatedJobItem[y].price = allocatedJobItem[y].price.toFixed(2);
                    allocatedJobItem[y].overhireRate = allocatedJobItem[y].overhireRate.toFixed(2);
                    allocatedJobItem[y].extraInvoiceItems = [];
                }
            }
            var filteredTrucks: TruckListOption[] = res.data.truckItems;
            filteredTrucks = filteredTrucks.filter(x => x.scheduleBranchId == scheduleBranchId);
            this.setState({
                runsheetChecked: res.data.runsheetChecked,
                canUpdateChecked: !res.data.runsheetChecked,
                saveDisabled: false,
                truckOptions: res.data.truckItems,
                truckFilterId: -1,
                truckTeam: "",
                allocatedJobs: allocatedJobs,
                filteredJobs: [],
                unallocatedJobs: res.data.unallocatedJobs,
                errors: {},
                loading: false
            })
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        change["saveDisabled"] = false;
        this.setState(change);
    }

    handleCheckboxChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.checked;
        change["runsheetCheckedChanged"] = true;
        change["saveDisabled"] = false;
        this.setState(change);

        if (!e.target.checked) {
            this.saveRunsheetChecked();
        }
    }
    
    saveRunsheetChecked = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var untickedRunsheet = {
            scheduleDate: this.state.scheduleDate,
            scheduleBranchId: this.state.branchFilterId,
            subId: user.sub
        }

        //save
        axios.post('api/RunSheet/SaveRunsheetUnchecked', untickedRunsheet, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Run Sheet Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //branch filter change
    handleBranchChange = (e: any) => {
        var branchFilterId = e.target.value * 1;
        this.setState({ branchFilterId: branchFilterId })
        if (this.state.systemCompanyId === 2) {
            this.getRunsheetData(branchFilterId);
        } else {
            this.getSimpleRunsheetData(branchFilterId);
        }
    }

    //truck filter change
    handleTruckChange = (e: any) => {
        var truckFilterId = e.target.value * 1;
        var filteredJobs: any[] = [];
        if (e.target.value > 0) {
            if (this.state.allocatedJobs.length > 0) {
                filteredJobs = this.state.allocatedJobs.filter(data => data.truckId == truckFilterId);
                this.setState({ truckFilterId: truckFilterId, filteredJobs: filteredJobs, saveDisabled: false, errors: {} });
            } else {
                filteredJobs = this.state.allocatedSimpleJobs.filter(data => data.truckId == truckFilterId);
                this.setState({ truckFilterId: truckFilterId, filteredSimpleJobs: filteredJobs, saveDisabled: false, errors: {} });
            }
            if (this.state.systemCompanyId === 2) {
                //get team details for Scaffold Trucks
                this.getTruckTeam(truckFilterId);
            }
        } else {
            this.setState({ truckFilterId: truckFilterId, truckTeam: "", saveDisabled: false });
        }
        
    }

    getTruckTeam = async (truckId: number) => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetTruckTeamByDate?ScheduleDate=' + this.state.scheduleDate + '&TruckId=' + truckId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ truckTeam: res.data });
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    //UPDATE LINKED JOBS
    showUpdateLinkedJobs = () => {
        var allocated = this.state.allocatedJobs.length > 0 ? this.state.allocatedJobs.filter(data => data.isDirty) : this.state.allocatedSimpleJobs.filter(data => data.isDirty);
        if (allocated.length === 0) {
            this.getLinkedJobs();
        } else {
            toast.error("Save your changes before updating linked jobs!");
        }
        
    }

    async getLinkedJobs() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/RunSheet/GetLinkedJobs?ScheduleDate=' + this.state.scheduleDate + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var linkedJobs = res.data.linkedJobs;
            for (var i = 0; i < linkedJobs.length; i++) {
                linkedJobs[i].itemIndex = i;
                //check if there is a departure time
                var scheduleId = linkedJobs[i].scheduleId;
                if (this.state.allocatedJobs.length > 0) {
                    //only update for company 2
                    var allocJob = this.state.allocatedJobs.find(a => a.scheduleId == scheduleId);
                    if (allocJob) {
                        linkedJobs[i].departureTime = allocJob.departureTime;
                        linkedJobs[i].departureTimeFormatted = allocJob.departureTimeFormatted;
                    }
                }

                let linkedJobItem = linkedJobs[i].items;
                for (var y = 0; y < linkedJobItem.length; y++) {
                    linkedJobItem[y].itemIndex = y;
                    linkedJobItem[y].isDirty = false;
                    linkedJobItem[y].price = linkedJobItem[y].price.toFixed(2);
                    linkedJobItem[y].overhireRate = linkedJobItem[y].overhireRate.toFixed(2);
                    linkedJobItem[y].extraInvoiceItems = [];
                }
            }
            this.setState({ linkedJobs: linkedJobs, showLinkedJobs: true, saveDisabled: false });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideLinkedJobs = () => {
        this.setState({ linkedJobs: [], showLinkedJobs: false });
    }

    updateLinkedJob = (linkedJobs: AllocatedJob[]) => {
        this.setState({ linkedJobs: linkedJobs });
    }

    saveLinkedJobs = () => {
        let items = this.state.linkedJobs;

        //create list of items that require rescheduling
        var rescheduleJobs = items.filter(data => data.isReschedule);
        var idList: number[] = [];
        for (var i = 0; i < rescheduleJobs.length; i++) {
            //for each job, get the items that require rescheduling
            for (var y = 0; y < rescheduleJobs[i].items.length; y++) {
                let rescheduleItem = rescheduleJobs[i].items[y];
                let kwikstageItems = rescheduleItem.kwikstageItems;
                if (kwikstageItems && kwikstageItems.length > 0) {
                    //need to check the kwistage items not the normal items
                    for (var z = 0; z < kwikstageItems.length; z++) {
                        if (kwikstageItems[z].reschedule) {
                            idList.push(rescheduleItem.id);
                        }
                    }
                } else {
                    if (rescheduleItem.reschedule) {
                        //need to check if the item is set to reschedule 
                        //as can have multiple items per job where only 1 may be set to reschedule
                        idList.push(rescheduleItem.id);
                    }
                }
            }
        }

        //only save the items that have changed
        var linked = items.filter(data => data.isDirty);
        if (linked.length > 0) {
            this.saveLinkedJobList(linked, idList);
        } else if (idList.length > 0) {
            //rescheduling whole item so don't need to save
            this.props.saveReschedule({ idList: idList });
        }
        else {
            toast.info("No items have been updated");
        }
    }

    async saveLinkedJobList(linked: AllocatedJob[], idList: number[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var linkedJobs = {
            scheduleDate: this.state.scheduleDate,
            jobs: linked,
            subId: user.sub
        }

        //save
        axios.post('api/RunSheet/SaveLinkedJobs', linkedJobs, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Run Sheet Update Saved");
            if (idList.length > 0) {
                this.props.saveReschedule({ idList: idList });
            } else {
                //reload runsheet
                this.setState({ linkedJobs: [], showLinkedJobs: false, saveDisabled: false });
                if (this.state.systemCompanyId === 2) {
                    this.getRunsheetData(this.state.branchFilterId);
                } else {
                    this.getSimpleRunsheetData(this.state.branchFilterId);
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END UPDATE LINKED JOBS

    //edit job
    getEditedJob = () => {
        if (this.state.fromLinkedJob) {
            var linkedJobs = this.state.linkedJobs;
            return linkedJobs;
        } else {
            var allocatedJobs = this.state.allocatedJobs;
            return allocatedJobs;
        }
    }

    editLinkedJob = (jobIndex: number, itemIndex: number) => {
        var linkedJobs = this.state.linkedJobs;
        var linkedJob = linkedJobs[jobIndex];
        var linkedItem = linkedJob.items[itemIndex];

        this.setState({ showEditJobModal: true, scheduleId: linkedItem.id, jobIndex: jobIndex, itemIndex: itemIndex });
    }

    editJob = (e: any, jobIndex: number, itemIndex: number) => {
        e.preventDefault();
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[jobIndex];
        var allocatedItem = allocatedJob.items[itemIndex];

        this.setState({ showEditJobModal: true, scheduleId: allocatedItem.id, jobIndex: jobIndex, itemIndex: itemIndex });
    }

    editSimpleJob = (e: any, jobIndex: number) => {
        e.preventDefault();

        var simpleJobs = this.state.allocatedSimpleJobs;
        var simpleJob = simpleJobs[jobIndex];

        this.setState({ showEditJobModal: true, scheduleId: simpleJob.scheduleId, jobIndex: jobIndex });  
    }

    saveEditJob = (quantityScheduled: number, quantityTotal: number, item: string, itemShortcut: string, rowVersionTimestamp: Uint8Array) => {
        if (this.state.fromLinkedJob) {
            var linkedJobs = this.state.linkedJobs;
            var linkedJob = linkedJobs[this.state.jobIndex];
            var linkedItem = linkedJob.items[this.state.itemIndex];

            linkedItem.quantityScheduled = quantityScheduled;
            linkedItem.quantityTotal = quantityTotal;
            linkedItem.item = item;
            linkedItem.rowVersionTimestamp = rowVersionTimestamp;

            this.setState({ linkedJobs: linkedJobs, showEditJobModal: false, showLinkedJobs: true, saveDisabled: false, scheduleId: 0, jobIndex: -1, itemIndex: -1 });
        } else {
            if (this.state.systemCompanyId === 2) {
                var allocatedJobs = this.state.allocatedJobs;
                var allocatedJob = allocatedJobs[this.state.jobIndex];
                var allocatedItem = allocatedJob.items[this.state.itemIndex];

                allocatedItem.quantityScheduled = quantityScheduled;
                allocatedItem.quantityTotal = quantityTotal;
                allocatedItem.item = item;
                allocatedItem.rowVersionTimestamp = rowVersionTimestamp;

                this.setState({ allocatedJobs: allocatedJobs, showEditJobModal: false, showLinkedJobs: false, saveDisabled: false, scheduleId: 0, jobIndex: -1, itemIndex: -1 });
            } else {
                var simpleJobs = this.state.allocatedSimpleJobs;
                var simpleJob = simpleJobs[this.state.jobIndex];
                simpleJob.quantityScheduled = quantityScheduled;
                simpleJob.quantityTotal = quantityTotal;
                simpleJob.item = item;
                simpleJob.itemShortcut = itemShortcut;
                simpleJob.rowVersionTimestamp = rowVersionTimestamp;

                this.setState({ allocatedSimpleJobs: simpleJobs, showEditJobModal: false, showLinkedJobs: false, saveDisabled: false, scheduleId: 0, jobIndex: -1 });
            }
        }
    }

    hideEditJobModal = () => {
        this.setState({ showEditJobModal: false, scheduleId: 0, jobIndex: -1, saveDisabled: false, errors: {} });
    }
    //end edit job modal

    //ADD UNIT NUMBER
    addUnitNumberSimple = (e: any, itemIndex: number) => {
        e.preventDefault();

        var simpleJobs = this.state.allocatedSimpleJobs;
        var simpleJob = simpleJobs[itemIndex];

        this.setState({ jobIndex: itemIndex, scheduleId: simpleJob.scheduleId, showAddUnitNumber: true });
    }

    hideAddUnitNumberSimple = () => {
        this.setState({ jobIndex: -1, scheduleId: -1, showAddUnitNumber: false });
    }

    saveAddUnitNumberSimple = (unitNumber: string, rowVersionTimestamp: Uint8Array) => {
        var simpleJobs = this.state.allocatedSimpleJobs;
        var simpleJob = simpleJobs[this.state.jobIndex];
        simpleJob.itemUnitNumber = unitNumber;
        simpleJob.rowVersionTimestamp = rowVersionTimestamp;

        this.setState({ jobIndex: -1, scheduleId: -1, showAddUnitNumber: false, allocatedSimpleJobs: simpleJobs });
    }

    //END ADD UNIT NUMBER

    //EDIT DUNNY NOTES
    hideEditSimpleNotes = () => {
        this.setState({ showSimpleNotes: false, scheduleId: -1, jobIndex: -1 });
    }

    editSimpleNotes = (e: any, jobIndex: number) => {
        e.preventDefault();
        var allocatedJobs = this.state.allocatedSimpleJobs;
        var allocatedJob = allocatedJobs[jobIndex];
        this.setState({ showSimpleNotes: true, scheduleId: allocatedJob.scheduleId, jobIndex: jobIndex, errors: {} });
    }

    saveSimpleNotes = (scheduleNotes: string, timestamp: Uint8Array) => {
        var allocatedJobs = this.state.allocatedSimpleJobs;
        var allocatedJob = allocatedJobs[this.state.jobIndex];

        toast.success("Run Sheet Notes Saved");

        allocatedJob.rowVersionTimestamp = timestamp;
        allocatedJob.scheduleNotes = scheduleNotes;

        this.setState({ showSimpleNotes: false, allocatedSimpleJobs: allocatedJobs, scheduleId: -1, jobIndex: -1, errors: {} });

    }
    //END EDIT DUNNY NOTES

    //EDIT SCAFFOLD NOTES - Scheduling notes = 1 and Driver Notes = 2
    resetEditNotes = () => {
        var editNotes: EditNotes = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        };
        return editNotes;
    }

    hideNotes = () => {
        var editNotes = this.resetEditNotes();

        this.setState({ editNotes: editNotes, showNotes: false, showNoteType: 0, saveDisabled: false });
    }

    updateNotes = (editNote: EditNotes) => {
        var editNotes = this.state.editNotes;
        editNotes.notes = editNote.notes;
        this.setState({
            editNotes: editNotes,
            saveDisabled: false
        });
    }

    editNotes = (e: any, jobIndex: number, itemIndex: number, noteType: number) => {
        e.preventDefault();
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[jobIndex];

        var editNotes: EditNotes;
        if (noteType == 1) {
            //edit driver notes
            editNotes = {
                heading: "Driver Notes",
                parentIndex: 0,
                rowIndex: jobIndex,
                id: allocatedJob.id,
                itemId: 0,
                item: "",
                notes: allocatedJob.driverNotes ? allocatedJob.driverNotes : ""
            }
        } else {
            //edit schedule notes
            var allocatedJobItem = allocatedJob.items[itemIndex];

            editNotes = {
                heading: "Schedule Notes",
                parentIndex: jobIndex,
                rowIndex: itemIndex,
                id: allocatedJobItem.id,
                itemId: allocatedJobItem.itemId,
                item: allocatedJobItem.item,
                notes: allocatedJobItem.scheduleNotes ? allocatedJobItem.scheduleNotes : ""
            }

        }
        this.setState({ editNotes: editNotes, showNotes: true, showNoteType: noteType, saveDisabled: false, errors: {} });
    }

    saveNotes = () => {
        var editNotes = this.state.editNotes;
        var allocatedJobs = this.state.allocatedJobs;

        if (this.state.showNoteType == 1) {
            //driver notes
            var allocatedJob = allocatedJobs[editNotes.rowIndex];
            allocatedJob.driverNotes = editNotes.notes;
            this.setState({ allocatedJobs: allocatedJobs });
            if (allocatedJob.id > 0) {
                this.saveDriverNotes(allocatedJob.id, allocatedJob.jobId, editNotes.notes);
            } else {
                var resetNotes = this.resetEditNotes();
                this.setState({ editNotes: resetNotes, showNotes: false, showNoteType: 0, saveDisabled: false, errors: {} });
            }
        } else {
            this.saveRunsheetScheduleNotes(editNotes);
        }
    }

    saveDriverNotes = async (id: number, jobId: number, notes: string) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //only save notes if id is set (otherwise will be saved when click the Save button)
        var driverNotes = {
            Id: id,
            JobId: jobId,
            Notes: notes,
            SubId: user.sub
        };
        //save
        axios.post('api/RunSheet/SaveDriverNotes', driverNotes, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Driver Notes Saved");
            var resetNotes: EditNotes = {
                heading: "",
                rowIndex: 0,
                parentIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            }
            this.setState({ editNotes: resetNotes, showNotes: false, showNoteType: 0, saveDisabled: false, errors: {} });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveRunsheetScheduleNotes = async (editNotes: EditNotes) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var allocatedJobs = this.state.allocatedJobs;

        //schedule notes
        var allocatedJob = allocatedJobs[editNotes.parentIndex];
        var allocatedJobItem = allocatedJob.items[editNotes.rowIndex];
        allocatedJobItem.scheduleNotes = editNotes.notes;

        var notes: ScheduleNotes = {
            Id: allocatedJobItem.id,
            Notes: editNotes.notes,
            ItemId: editNotes.itemId,
            Item: allocatedJobItem.item,
            SubId: user.sub
        };

        //save
        axios.post('api/RunSheet/SaveNotes', notes, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Schedule Notes Saved");

            var resetNotes: EditNotes = {
                heading: "",
                rowIndex: 0,
                parentIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            }
            //update the timestamp
            allocatedJobItem.rowVersionTimestamp = res.data.rowVersionTimestamp;

            this.setState({ allocatedJobs: allocatedJobs, editNotes: resetNotes, showNotes: false, showNoteType: 0, saveDisabled: false, errors: {} });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END EDIT SCAFFOLD NOTES

    //PLANS AND PHOTOS
    viewPhotos = (e: any, runsheetId: number) => {
        e.preventDefault();
        this.getPhotos(runsheetId);
    }

    async getPhotos(runsheetId: number) {
        const token = await authService.getAccessToken();

        axios.get('api/RunSheet/GetPhotos?RunsheetId=' + runsheetId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ photos: res.data, showPhotos: true, photoIndex: 0, saveDisabled: false });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    updatePhotoIndex = (photoIndex: number) => {
        this.setState({ photoIndex: photoIndex });
    }

    hidePhotos = () => {
        this.setState({ photos: [], photoIndex: 0, showPhotos: false });
    }

    //INVOICE ITEMS
    //add callout fee invoice
    addCalloutInvoiceItems = (e: any, itemIndex: number) => {
        e.preventDefault();

        //update callout item
        this.onCellChange(itemIndex, "callout", e.target.checked);
        //if callout is ticked then show the form
        if (e.target.checked) {
            this.invoiceItemCheck(itemIndex, 2);
        }
    }

    addCalloutSimpleInvoiceItems = (e: any, itemIndex: number) => {
        e.preventDefault();

        //update callout item
        var jobs = this.state.allocatedSimpleJobs;
        var job = jobs[itemIndex];
        job.callout = e.target.checked;
        job.reschedule = e.target.checked ? true : false;  //callout items need to be rescheduled or placed on hold
        job.onHold = false;
        job.quantity = 0;
        job.quantityLost = 0;
        job.fencePanels = 0;
        job.fencePanelsDamaged = 0;
        job.fenceBlocks = 0;
        job.fenceClamps = 0;
        job.fencePickets = 0;
        job.fenceSigns = 0;
        job.fenceBraces = 0;
        job.isDirty = true;

        this.setState({ allocatedSimpleJobs: jobs, errors: {} });

        //if callout is ticked then show the form
        if (e.target.checked) {
            var invoiceItem = this.state.invoiceItem;
            var invItem = this.addBlankInvoiceItem(job.scheduleId);
            invoiceItem.invoiceItems.push(invItem);

            this.setState({ invoiceItemParentIndex: itemIndex, showInvoiceItemModal: true, invoiceItem: invoiceItem, invoiceItemModalType: 3, saveDisabled: false });
        }
    }

    invoiceItemCheck = (jobIndex: number, invoiceItemModalType: number) => {
        //check if scheduling item is valid before allowing invoice items to be added
        var scheduledJob = this.state.allocatedJobs[jobIndex];

        if ((scheduledJob.jobNumber == 1 && scheduledJob.departureTimeFormatted == "") || scheduledJob.siteArrivalTimeFormatted == "" || scheduledJob.siteCompletionTimeFormatted == "") {
            toast.error("You must enter the run sheet details before adding extra invoicing items!");
            if (invoiceItemModalType == 2) {
                //set callout back to false, as need to update the details first
                var items = this.state.allocatedJobs;
                var item = items[jobIndex];
                item.callout = false;
                this.setState({ allocatedJobs: items });
            }
        } else {
            //add blank invoice item
            var invoiceItem = this.state.invoiceItem;
            var invItem = this.addBlankInvoiceItem(scheduledJob.scheduleId);
            invoiceItem.invoiceItems.push(invItem);

            this.setState({ invoiceItemParentIndex: jobIndex, showInvoiceItemModal: true, invoiceItem: invoiceItem, invoiceItemModalType: invoiceItemModalType, saveDisabled: false });
        }
    }

    addBlankInvoiceItem = (scheduleId:number) => {
        var invItem: InvoiceItems = {
            id: 0,
            rowIndex: 0,
            active: true,
            itemId: -1,
            itemTypeId: -1,
            jobQuoteItemId: 0,
            scheduleId: scheduleId,
            description: "",
            rate: 0,
            gst: 0,
            amount: 0
        };
        return invItem;
    }

    //add extra invoice items
    addInvoiceItems = (e: any, jobIndex: number) => {
        e.preventDefault();
        var scheduledJob = this.state.allocatedJobs[jobIndex];

        //add blank invoice item
        var invoiceItem = this.state.invoiceItem;
        var invItem = this.addBlankInvoiceItem(scheduledJob.scheduleId);
        invoiceItem.invoiceItems.push(invItem);

        this.setState({ invoiceItemParentIndex: jobIndex, showInvoiceItemModal: true, invoiceItem: invoiceItem, invoiceItemModalType: 1 });
    }

    addInvoiceItemsSimple = (e: any, jobIndex: number) => {
        e.preventDefault();
        var scheduledJob = this.state.allocatedSimpleJobs[jobIndex];

        //add blank invoice item
        var invoiceItem = this.state.invoiceItem;
        var invItem = this.addBlankInvoiceItem(scheduledJob.scheduleId);
        invoiceItem.invoiceItems.push(invItem);

        this.setState({ invoiceItemParentIndex: jobIndex, showInvoiceItemModal: true, invoiceItem: invoiceItem, invoiceItemModalType: 4 });
    }

    resetInvoiceItem = () => {
        var invoiceItem: InvoiceItem = {
            invoiceItems: [],
            calloutNotes: "",
            noCalloutInvoiceNotes: ""
        };

        return invoiceItem;
    }

    //invoice items
    hideInvoiceModal = () => {
        var invoiceItem = this.resetInvoiceItem();

        //set callout back to false, as need to update the details first
        if (this.state.invoiceItemModalType === 3) {
            var jobs = this.state.allocatedSimpleJobs;
            var job = jobs[this.state.invoiceItemParentIndex];
            job.calloutItem = false;

            this.setState({ allocatedSimpleJobs: jobs, invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
        } else if (this.state.invoiceItemModalType === 2) {
            var items = this.state.allocatedJobs;
            var item = items[this.state.invoiceItemParentIndex];
            item.callout = false;

            this.setState({ allocatedJobs: items, invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
        } else {
            this.setState({ invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
        }
        
    }

    updateInvoiceItems = (invoiceItem: InvoiceItem) => {
        this.setState({ invoiceItem: invoiceItem });
    }

    saveInvoiceItems = () => {
        if (this.state.invoiceItemModalType === 3 || this.state.invoiceItemModalType === 4) {
            this.saveInvoiceSimpleCalloutItems();
        } else {
            this.saveInvoiceCalloutItems();
        }
        
    }

    async saveInvoiceSimpleCalloutItems() {
        var invoiceItem = this.state.invoiceItem;
        var invoiceItems = invoiceItem.invoiceItems;

        var allocatedJobs = this.state.allocatedSimpleJobs;
        var scheduleItem = allocatedJobs[this.state.invoiceItemParentIndex];

        if (scheduleItem) {
            //add invoicing items to the scheduled item
            var extraInvoiceItems: ExtraInvoiceItem[] = [];
            for (var i = 0; i < invoiceItems.length - 1; i++) {
                var extraInvoiceItem = {
                    scheduleId: scheduleItem.scheduleId,
                    itemId: scheduleItem.itemId,
                    itemTypeId: invoiceItems[i].itemTypeId,
                    rate: invoiceItems[i].rate,
                    gst: invoiceItems[i].gst,
                    amount: invoiceItems[i].amount,
                    description: invoiceItems[i].description
                };
                extraInvoiceItems.push(extraInvoiceItem);
            }
            scheduleItem.extraInvoiceItems = extraInvoiceItems;
            scheduleItem.isDirty = false;
            this.setState({ allocatedSimpleJobs: allocatedJobs });

            const user = await authService.getUser();
            const token = await authService.getAccessToken();

            if (this.state.invoiceItemModalType === 4) {
                var runSheet = {
                    jobId: scheduleItem.jobId,
                    items: scheduleItem.extraInvoiceItems,
                    subId: user.sub
                };

                //save
                axios.post('api/RunSheet/SaveExtraInvoiceItems', runSheet, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    toast.success("Extra Item Invoice Created");

                    //reset
                    invoiceItem = this.resetInvoiceItem();
                    this.setState({ invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
                })
                .catch(error => {
                    toast.error(error.message);
                });
            }
            else {
                let callout = {
                    scheduleId: scheduleItem.scheduleId,
                    jobId: scheduleItem.jobId,
                    truck: scheduleItem.truck,
                    scheduleDate: this.state.scheduleDate,
                    reasonForCallout: invoiceItem.calloutNotes,
                    reasonNoCalloutInvoice: invoiceItem.noCalloutInvoiceNotes,
                    extraInvoiceItems: extraInvoiceItems,
                    subId: user.sub
                };

                //save callout invoice
                axios.post('api/RunSheet/SaveCalloutSimpleInvoice', callout, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    toast.success("Callout Invoice Saved");
                    //reset
                    var invoiceItem = this.resetInvoiceItem();
                    this.setState({ invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0 });
                    //for (var y = 0; y < allocatedJobs.length; y++) {
                    //    if (allocatedJobs[y].reschedule) {
                    //        var idList: number[] = [];
                    //        idList.push(allocatedJobs[y].scheduleId);
                    //        this.props.saveReschedule({ idList: idList });
                    //    }
                    //}
                })
                .catch(error => {
                    toast.error(error.message);
                });
            }
        }


    }

    async saveInvoiceCalloutItems() {
        var invoiceItem = this.state.invoiceItem;
        var invoiceItems = invoiceItem.invoiceItems;

        var allocatedJobs = this.state.allocatedJobs;
        var scheduleItem = allocatedJobs[this.state.invoiceItemParentIndex];
        if (scheduleItem) {
            //add invoicing items to the scheduled item
            var extraInvoiceItems: ExtraInvoiceItem[] = [];
            for (var i = 0; i < invoiceItems.length - 1; i++) {
                var extraInvoiceItem = {
                    scheduleId: scheduleItem.scheduleId,
                    itemTypeId: invoiceItems[i].itemTypeId,
                    rate: invoiceItems[i].rate,
                    gst: invoiceItems[i].gst,
                    amount: invoiceItems[i].amount,
                    description: invoiceItems[i].description
                };
                extraInvoiceItems.push(extraInvoiceItem);
            }
            scheduleItem.extraInvoiceItems = extraInvoiceItems;
            scheduleItem.isDirty = false;
            this.setState({ allocatedJobs: allocatedJobs });

            if (this.state.invoiceItemModalType < 2) {    
                this.saveExtraInvoiceItems(scheduleItem, invoiceItem);
            } else {
                const user = await authService.getUser();

                var callout: RunsheetCalloutItem = {
                    scheduleDate: this.state.scheduleDate,
                    job: scheduleItem,
                    reasonForCallout: invoiceItem.calloutNotes,
                    reasonNoCalloutInvoice: invoiceItem.noCalloutInvoiceNotes,
                    subId: user.sub
                };
                scheduleItem.calloutComplete = true;
                this.setState({ allocatedJobs: allocatedJobs, saveDisabled: false });
                this.saveCallout(callout);
            }
        }
    }

    async saveExtraInvoiceItems(scheduleItem: AllocatedJob, invoiceItem: InvoiceItem) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var runSheet = {
            jobId: scheduleItem.jobId,
            items: scheduleItem.extraInvoiceItems,
            subId: user.sub
        };

        //save
        axios.post('api/RunSheet/SaveExtraInvoiceItems', runSheet, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Extra Item Invoice Created");

            //reset
            invoiceItem = this.resetInvoiceItem();
            this.setState({ invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async saveCallout(callout: RunsheetCalloutItem) {
        const token = await authService.getAccessToken();

        //save callout invoice
        axios.post('api/RunSheet/SaveCalloutInvoice', callout, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Callout Invoice Saved");
            //reset
            var invoiceItem = this.resetInvoiceItem();

            this.setState({ invoiceItemParentIndex: 0, invoiceItem: invoiceItem, showInvoiceItemModal: false, invoiceItemModalType: 0, saveDisabled: false });
            var scheduleItems = callout.job.items;
            for (var y = 0; y < scheduleItems.length; y++) {
                if (scheduleItems[y].reschedule) {
                    var idList: number[] = [];
                    idList.push(scheduleItems[y].id);
                    this.props.saveReschedule({ idList: idList });
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END INVOICE ITEMS

    //ON SITE MINS
    openOnSiteMins = (e: any, itemIndex: number) => {
        e.preventDefault();

        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[itemIndex];
        var onSiteMinsEdit: EditOnSiteMinutes = {
            jobId: allocatedJob.jobId,
            runsheetId: allocatedJob.id,
            onSiteMinutes: allocatedJob.onSiteMins
        };

        this.setState({ showOnSiteMins: true, editOnSiteMins: onSiteMinsEdit, itemIndex: itemIndex });
    }

    hideOnSiteMinutes = () => {
        let onSiteMins = this.resetOnSiteMins();

        this.setState({ showOnSiteMins: false, editOnSiteMins: onSiteMins });
    }

    resetOnSiteMins = () => {
        let onSiteMins: EditOnSiteMinutes = {
            jobId: -1,
            runsheetId: -1,
            onSiteMinutes: 0
        };
        return onSiteMins;
    }

    saveOnSiteMinutes = (updatedOnSiteMinutes: number, updateOnSiteMinutes: boolean) => {
        if (updateOnSiteMinutes) {
            var allocatedJobs = this.state.allocatedJobs;
            var allocatedJob = allocatedJobs[this.state.itemIndex];
            allocatedJob.onSiteMins = updatedOnSiteMinutes;

            let onSiteMins = this.resetOnSiteMins();

            this.setState({ showOnSiteMins: false, editOnSiteMins: onSiteMins, allocatedJobs: allocatedJobs });
            toast.success("On Site Minutes updated successfully");
        } else {
            let onSiteMins = this.resetOnSiteMins();
            this.setState({ showOnSiteMins: false, editOnSiteMins: onSiteMins });
            toast.success("Times updated successfully");
        }
    }
    //END ON SITE MINS

    //grid item changed
    onCellChange = (itemIndex: number, item: string, value: any) => {
        //update item that has changed
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[itemIndex];
        let newAllocatedJob: any = {};
        newAllocatedJob = allocatedJob;
        newAllocatedJob[item] = value;
        allocatedJob.isDirty = true;
        
        this.setState({ allocatedJobs: allocatedJobs, saveDisabled: false, errors: {} });

        this.validateTimes(itemIndex);
    }

    onCellSimpleChange(e: any, itemIndex: number) {
        var jobs = this.state.allocatedSimpleJobs;
        var job = jobs[itemIndex];
        let newAllocatedJob: any = {};
        newAllocatedJob = job;
        newAllocatedJob[e.target.name] = e.target.value * 1;
        job.isDirty = true;

        this.setState({ allocatedSimpleJobs: jobs, saveDisabled: false, errors: {} });
    }

    onCellSimplePanelChange = (e: any, itemIndex: number) => {
        var jobs = this.state.allocatedSimpleJobs;
        var job = jobs[itemIndex];

        var value = e.target.value * 1;
        var totalPanels = value;
        if (e.target.name === "fencePanelsExtra") {
            job.fencePanelsExtra = value;
            totalPanels += job.fencePanels;
        } else {
            job.fencePanels = value;
            totalPanels += job.fencePanelsExtra;
        }
        job.isDirty = true;

        //calculate qty in lm from panels (2.4 m)
        var lmQty = totalPanels * 2.4;
        var actQty = Math.round(lmQty);
        var qtyDiff = job.quantityScheduled - actQty;

        //check if the actQty works out to within 2 l/m of the scheduled qty and if so round up/down to the scheduled qty
        if (qtyDiff >= -2 && qtyDiff <= 2) {
            //use scheduled qty
            actQty = job.quantityScheduled;
        }

        job.quantity = actQty;
        this.setState({ allocatedSimpleJobs: jobs, saveDisabled: false, errors: {} });
    }

    onCellCheckboxSimpleRescheduleChange(e: any, itemIndex: number) {
        var jobs = this.state.allocatedSimpleJobs;
        var job = jobs[itemIndex];
        let newAllocatedJob: any = {};
        newAllocatedJob = job;
        newAllocatedJob[e.target.name] = e.target.checked;
        job.isDirty = true;
        job.onHold = false;
        job.invoice = false;

        if ((job.quantityScheduled === 1 || job.quantity >= job.quantityScheduled) && e.target.checked) {
            //need to reset the qty back to 0 as it shouldn't be set
            job.quantity = 0;
            job.fencePanels = 0;
            job.fencePanelsDamaged = 0;
            job.fenceBlocks = 0;
            job.fenceClamps = 0;
            job.fencePickets = 0;
            job.fenceSigns = 0;
            job.fenceBraces = 0;
            job.size = job.sizeOriginal;
            job.extraHours = job.extraHoursOriginal;
            job.waterBoardNotified = false;
        }

        this.setState({ allocatedSimpleJobs: jobs, saveDisabled: false, errors: {} });
    }

    onCellCheckboxSimpleChange(e: any, itemIndex: number) {
        var jobs = this.state.allocatedSimpleJobs;
        var job = jobs[itemIndex];
        let newAllocatedJob: any = {};
        newAllocatedJob = job;
        newAllocatedJob[e.target.name] = e.target.checked;
        job.isDirty = true;

        //install callouts must be put on hold or rescheduled
        if (job.typeId === 1 && e.target.name === "onHold") {
            job.reschedule = e.target.checked ? false : true;
        } else if (e.target.name === "invoice") {
            job.reschedule = false;
        }

        this.setState({ allocatedSimpleJobs: jobs, saveDisabled: false, errors: {} });
    }

    updateJobQuantitySimple = () => {
        //update the actual quantity to match the scheduled quantity so they can easily tick all the jobs off as done
        let jobs = this.state.allocatedSimpleJobs;

        //get jobs for truck selected - can only update all if a truck is selected first
        if (this.state.truckFilterId > 0) {
            var truckJobs = jobs.filter(j => j.truckId == this.state.truckFilterId);
            for (var i = 0; i < truckJobs.length; i++) {
                var job = truckJobs[i];

                //don't update fencing or braces as they need sub-items completed
                if (job.quantity === 0 && job.itemTypeId != 50 && job.itemTypeId != 51 && !job.callout && !job.reschedule) {
                    //make sure we don't update the quantity for 1300 jobs that require unit numbers
                    if ((job.itemUnitNumber != "" && job.unitNumberRequired) || !job.unitNumberRequired) {
                        job.quantity = job.quantityScheduled;
                        job.isDirty = true;
                    }
                }
            }
            this.setState({ allocatedSimpleJobs: jobs, saveDisabled: false, errors: {} });
        }
    } 

    validateTimes = (itemIndex: number) => {
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[itemIndex];
        var rescheduleJobs = allocatedJob.items.filter(a => a.reschedule).length;

        var error = "";

        //check arrival > depart 
        if (allocatedJob.siteArrivalTimeFormatted && allocatedJob.departureTimeFormatted) {
            if (DateHelpers.validTimeAmount(allocatedJob.siteArrivalTimeFormatted) && DateHelpers.validTimeAmount(allocatedJob.departureTimeFormatted)) {
                if (!DateHelpers.compareTimeGreater(allocatedJob.departureTimeFormatted, allocatedJob.siteArrivalTimeFormatted)) {
                    error += "Arrival Time needs to be > than Departure Time \n";
                }
            }  
        }
        //check completion > arrival and completion - arrival is within estimated time +- 1 hr
        if (allocatedJob.siteArrivalTimeFormatted && allocatedJob.siteCompletionTimeFormatted) {
            if (DateHelpers.validTimeAmount(allocatedJob.siteArrivalTimeFormatted) && DateHelpers.validTimeAmount(allocatedJob.siteCompletionTimeFormatted)) {
                if (!DateHelpers.compareTimeGreater(allocatedJob.siteArrivalTimeFormatted, allocatedJob.siteCompletionTimeFormatted)) {
                    error += "Completion Time needs to be > than Arrival Time \n";
                } else if (allocatedJob.estTimeMins > 0) {
                    //check the estimated time
                    var timeDiffMins = DateHelpers.getTimeDifferenceMins(allocatedJob.siteArrivalTimeFormatted, allocatedJob.siteCompletionTimeFormatted);
                    var estTimeMins = allocatedJob.estTimeMins;

                    //check that time difference mins is not more than 1 hour > estimated time mins
                    var estTimeMins1Hr = estTimeMins + 60;
                    if (timeDiffMins > estTimeMins1Hr) {
                        error += "Difference between Arrival and Completion times is > Estimated Time (+ 1 hr) \n";
                    }

                    //check that time difference mins is not more than 1 hr < estimated time mins
                    //ignore this rule if we are rescheduling some items as the estimated time will be incorrect
                    if (estTimeMins > 60 && rescheduleJobs === 0) {
                        var estTimeMins1Hr = estTimeMins - 60;
                        if (timeDiffMins < estTimeMins1Hr) {
                            error += "Difference between Arrival and Completion times is < Estimated Time (- 1 hr) \n";
                        }
                    } 
                }
            }
        }
        //check if hours outside normal hours
        if (allocatedJob.departureTimeFormatted) {
            if (DateHelpers.validTimeAmount(allocatedJob.departureTimeFormatted) && !DateHelpers.isTimeInBusinessHours(allocatedJob.departureTimeFormatted)) {
                error += "Departure Time is not within business hours \n"
            }
        }
        if (allocatedJob.siteArrivalTimeFormatted) {
            if (DateHelpers.validTimeAmount(allocatedJob.siteArrivalTimeFormatted) && !DateHelpers.isTimeInBusinessHours(allocatedJob.siteArrivalTimeFormatted)) {
                error += "Arrival Time is not within business hours \n"
            }
        }
        if (allocatedJob.siteCompletionTimeFormatted) {
            if (DateHelpers.validTimeAmount(allocatedJob.siteCompletionTimeFormatted) && !DateHelpers.isTimeInBusinessHours(allocatedJob.siteCompletionTimeFormatted)) {
                error += "Completed Time is not within business hours \n"
            }
        }

        if (error) {
            //remove the last \n
            error = error.substring(0, error.length - 2);
        }

        allocatedJob.error = error;
        this.setState({ allocatedJobs: allocatedJobs });
    }

    onSubgridCellChange = (jobIndex: number, itemIndex: number, item: string, value: any) => {
        //update item that has changed
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[jobIndex];
        var allocatedItem = allocatedJob.items[itemIndex];
        let newAllocatedItem: any = {};
        newAllocatedItem = allocatedItem;
        newAllocatedItem[item] = value;
        if (item != "reschedule") {
            //need users to have the ability to reschedule an item without updating the details (as job may not have been done)
            allocatedItem.isDirty = true;
            //update the job as isDirty too so validation works
            allocatedJob.isDirty = true;
        } else {
            //set the job reschedule flag - for saving
            //check to see if any items are set to reschedule and update main job
            var jobsReschedule = allocatedJob.items.filter(ks => ks.reschedule);
            if (jobsReschedule && jobsReschedule.length > 0) {
                allocatedJob.isReschedule = true;
            } else {
                allocatedJob.isReschedule = false;
            }
        }

        this.setState({ allocatedJobs: allocatedJobs, saveDisabled: false, errors: {} });
    }

    onKwikstageSubgridCellChange = (e: any, jobIndex: number, itemIndex: number, kwikstateIndex: number) => {
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[jobIndex];
        var allocatedItem = allocatedJob.items[itemIndex];
        var kwikstageItem = allocatedItem.kwikstageItems[kwikstateIndex];

        let newkwikstageItem: any = {};
        newkwikstageItem = kwikstageItem;
        newkwikstageItem[e.target.name] = e.target.value;

        allocatedItem.isDirty = true;
        //update the job as isDirty too so validation works
        allocatedJob.isDirty = true;
        
        this.setState({ allocatedJobs: allocatedJobs, saveDisabled: false, errors: {} });
    }

    onKwikstageSubgridCheckChange = (e: any, jobIndex: number, itemIndex: number, kwikstateIndex: number) => {
        var allocatedJobs = this.state.allocatedJobs;
        var allocatedJob = allocatedJobs[jobIndex];
        var allocatedItem = allocatedJob.items[itemIndex];
        var kwikstageItem = allocatedItem.kwikstageItems[kwikstateIndex];

        let newkwikstageItem: any = {};
        newkwikstageItem = kwikstageItem;
        newkwikstageItem[e.target.name] = e.target.checked;

        //check to see if any items are set to reschedule and update main job
        var jobsReschedule = allocatedItem.kwikstageItems.filter(ks => ks.reschedule);
        if (jobsReschedule && jobsReschedule.length > 0) {
            allocatedJob.isReschedule = true;
        } else {
            allocatedJob.isReschedule = false;
        }

        this.setState({ allocatedJobs: allocatedJobs, saveDisabled: false, errors: {} });
    }

    refresh = () => {
        if (this.state.systemCompanyId === 2) {
            this.getRunsheetData(this.state.branchFilterId);
        } else {
            this.getSimpleRunsheetData(this.state.branchFilterId);
        }
    }

    printSwms = (e: any, itemIndex: number) => {
        e.preventDefault();

        var allocatedJobs = this.state.allocatedJobs;
        var job = allocatedJobs[itemIndex];

        this.downloadSwms(this.state.scheduleDate, job.truckId, job.jobId);
    }

    async downloadSwms(scheduleDate: string, vehicleId: number, jobId: number) {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetScheduleSwms?ScheduleDate=' + scheduleDate + "&vehicleId=" + vehicleId + "&jobId=" + jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                var swms = res.data.reportData;
                if (swms.length > 0) {
                    for (let x = 0; x < swms.length; x++) {
                        let report = new Stimulsoft.Report.StiReport();
                        let reportFile = Stimulsoft.System.IO.File.getFile("reports/" + res.data.reportFileName);
                        report.load(reportFile);

                        report.dictionary.dataStore.clear();

                        let reportData = JSON.stringify(swms[x]);
                        //Dataset created
                        let dataSet = new Stimulsoft.System.Data.DataSet("data");
                        dataSet.readJson(reportData)
                        report.regData(dataSet.dataSetName, "", dataSet);
                        report.reportName = res.data.reportName;
                        report.dictionary.connect(false);
                        report.dictionary.synchronize();

                        //add relationships between data
                        for (var i = 0; i < res.data.reportRelationships.length; i++) {
                            var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(res.data.reportRelationships[i].sourceName, res.data.reportRelationships[i].name, res.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(res.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(res.data.reportRelationships[i].childTableName), [res.data.reportRelationships[i].parentFieldName], [res.data.reportRelationships[i].childFieldName]);
                            report.dictionary.relations.add(dataRelation);
                            report.dictionary.synchronize();
                        }
                        report.render();

                        let fileName = res.data.reportName + "-" + (x + 1);

                        timeout = setTimeout(this.downloadReportToPdf, 1000, report, fileName);
                    }
                }
            })
            .catch(error => {
                toast.error(error.message);
            });
    }

    downloadReportToPdf(report: any, fileName: string) {
        report.exportDocumentAsync(function (pdfData: any) {
            // Save data to file
            Stimulsoft.System.StiObject.saveAs(pdfData, fileName + ".pdf", "application/pdf");
        }, Stimulsoft.Report.StiExportFormat.Pdf);
    }

    validate = () => {
        let errors: any = {};
        let formIsValid = true;

        //if runsheet checked tickbox is checked then we need to validate and save all jobs
        let jobs = this.state.runsheetChecked ? this.state.allocatedJobs : this.state.truckFilterId > 0 ? this.state.filteredJobs : this.state.allocatedJobs;
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < jobs.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            //only validate items that have been changed
            if (jobs[i].isDirty || this.state.runsheetChecked) {
                //check if items only hold manual items, if so, then times not mandatory
                var manualItemsOnly = true;
                for (var y = 0; y < jobs[i].items.length; y++) {
                    if (!jobs[i].items[y].manualItem) {
                        manualItemsOnly = false;
                    }
                }

                if (!manualItemsOnly) {
                    //departure time only entered for the first job
                    if (jobs[i].jobNumber == 1 && jobs[i].departureTimeFormatted == "") {
                        newErrorString += "Departure Time, ";
                        error = true;
                    }
                    if (jobs[i].siteArrivalTimeFormatted == "") {
                        newErrorString += "Site Arrival Time, ";
                        error = true;
                    }
                    if (jobs[i].downTime && !DateHelpers.validTimeAmount(jobs[i].downTime)) {
                        newErrorString += "Valid Down Time, ";
                        error = true;
                    }
                    if (jobs[i].siteCompletionTimeFormatted == "") {
                        newErrorString += "Site Completion Time, ";
                        error = true;
                    }
                }

                for (var y = 0; y < jobs[i].items.length; y++) {
                    let totalqty = (jobs[i].items[y].quantity * 1) + (jobs[i].items[y].quantityLost * 1);

                    if (!jobs[i].callout) {
                        //only check quantity for jobs that aren't a callout as callout will be 0
                        if (jobs[i].items[y].itemIsKwikstage && jobs[i].items[y].kwikstageItems.length > 0) {
                            //kwikstage uses bays for totals instead of qty
                            for (var z = 0; z < jobs[i].items[y].kwikstageItems.length; z++) {
                                let totalqty = (jobs[i].items[y].kwikstageItems[z].baysComplete * 1) + (jobs[i].items[y].kwikstageItems[z].baysLost * 1);
                                if (!jobs[i].items[y].kwikstageItems[z].reschedule && totalqty != jobs[i].items[y].kwikstageItems[z].bays) {
                                    newErrorString += "Item " + (z + 1) + " - Actual Total + Lost Quantity must total Quantity Scheduled unless you are rescheduling to another day, ";
                                    error = true;
                                }
                            }

                        } else {
                            if (jobs[i].items[y].truckAssistScheduleId === 0) {
                                //assist jobs will have a quantity of 0 so ignore those jobs
                                if (!jobs[i].items[y].reschedule && jobs[i].items[y].quantity < 1 && jobs[i].items[y].quantityLost == 0) {
                                    newErrorString += "Item " + (y + 1) + " - Actual Total, ";
                                    error = true;
                                }
                            }

                            if (!jobs[i].items[y].reschedule && totalqty != jobs[i].items[y].quantityScheduled) {
                                newErrorString += "Item " + (y + 1) + " - Actual Total + Lost Quantity must total Quantity Scheduled unless you are rescheduling to another day, ";
                                error = true;
                            }
                        }

                        if (jobs[i].items[y].reschedule && !jobs[i].items[y].rescheduleComplete && totalqty === jobs[i].items[y].quantityScheduled) {
                            newErrorString += "Item " + (y + 1) + " - When rescheduling Actual Total must be ONLY the amount that was installed/picked up from site, ";
                            error = true;
                        }
                    }
                }
            } else { 
                //check if anything ticked reschedule (not already completed) and total qty + lost = schedule qty
                var rescheduledItems = jobs[i].items.filter(j => j.reschedule && !j.rescheduleComplete);
                for (var j = 0; j < rescheduledItems.length; j++) {
                    var resch = rescheduledItems[j];
                    let totalqty = (resch.quantity * 1) + (resch.quantityLost * 1);
                    if (resch.reschedule && !resch.rescheduleComplete && totalqty === resch.quantityScheduled) {
                        newErrorString += "Item " + (resch.itemIndex + 1) + " - When rescheduling Actual Total must be ONLY the amount that was installed/picked up from site, ";
                        error = true;
                    }
                }
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
            
        }
        if (error) {
            formIsValid = false;
            errors["allocatedItems"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    validateSimple = () => {
        let jobs = this.state.truckFilterId > 0 ? this.state.filteredSimpleJobs : this.state.allocatedSimpleJobs;

        let errors: any = {};
        let formIsValid = true;

        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < jobs.length; i++) {
            rowNumber += 1;
            var job = jobs[i];

            if (job.isDirty) {
                //validate the job
                var checkSimple = this.checkSimpleJobs(job);
                if (checkSimple.error) {
                    //update so we know there is data to fix
                    error = true;
                }

                if (checkSimple.errorMessage != "") {
                    //update the error message
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + checkSimple.errorMessage;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
        }
        if (error) {
            formIsValid = false;
            errors["allocatedItems"] = errorString;
        }
        this.setState({ errors: errors });

        //if truck filter on, check if other jobs changed and validate those
        if (this.state.truckFilterId > 0) {
            let otherJobs = this.state.allocatedSimpleJobs.filter(j => j.isDirty && j.truckId != this.state.truckFilterId);
            var otherError = false;
            var otherErrorMessage = "Check jobs allocated to other trucks:";
            for (var i = 0; i < otherJobs.length; i++) {
                var otherJob = otherJobs[i];
                var otherCheck = this.checkSimpleJobs(otherJob);

                if (otherCheck.error) {
                    otherError = true;
                    otherErrorMessage += "\n";
                    otherErrorMessage += "Check job " + otherJob.jobId + " on truck " + otherJob.truck + ". Error: " + otherCheck.errorMessage;
                }
            }

            if (otherError) {
                formIsValid = false;
                //add error to screen
                errors["allocatedItemsOther"] = otherErrorMessage;
                this.setState({ errors: errors });
            }
        }

        return formIsValid;
    }

    checkSimpleJobs = (job: AllocateSimpleJob) => {
        var newErrorString = "";
        var error = false;

        //QTY Check
        let totalqty = (job.quantity * 1) + (job.quantityLost);
        if (job.itemTypeId === 50 && !job.callout && !job.reschedule) {
            //fencing have different rules
            if (totalqty != job.quantityScheduled && !job.invoice) {
                newErrorString += "Number of Fence Panels needs to match Actual Qty to Qty unless you are rescheduling or invoicing, ";
                error = true;
            }
        } else if (job.itemTypeId === 26 && !job.callout && !job.reschedule) {
            //need to cater for drivers to only p&c some of the toilets and not all - can't go over the scheduled qty though
            if (totalqty != job.quantityScheduled && !job.invoice) {
                newErrorString += "Actual Qty + Lost Qty must total Qty Scheduled unless you are rescheduling or invoicing, ";
                error = true;
            } else if ((totalqty > job.quantityScheduled || totalqty == 0) && job.invoice) {
                newErrorString += "Actual Qty must be > 0 and < Qty Scheduled, ";
                error = true;
            }
        } else if (!job.callout && !job.reschedule) {
            if (totalqty != job.quantityScheduled && !job.invoice) {
                newErrorString += "Actual Qty + Lost Qty must total Qty Scheduled unless you are rescheduling to another day, ";
                error = true;
            }
        }

        if (job.itemTypeId === 59 && !job.callout && !job.reschedule) {
            //need to make sure the water board has been notified
            if (!job.waterBoardNotified) {
                newErrorString += "Water Board must be notified, ";
                error = true;
            }
        }

        if (job.itemTypeId === 61 && job.epaTransportCertificateCheckRequired && !job.callout && !job.reschedule) {
            if (!job.epaTransportCertificateChecked) {
                newErrorString += "Epa Transport Certificate MUST be checked to match litres, ";
                error = true;
            }
        }

        //Qty Check for Callouts - should be 0
        if (job.callout && totalqty > 0) {
            newErrorString += "Callouts should have 0 Actual Qty, ";
            error = true;
        }

        //Qty Check for Rescheduling - should be 0 (reschedule whole job) or < scheduled amt - reschedule remaining
        if (job.reschedule && totalqty >= job.quantityScheduled) {
            newErrorString += "When rescheduling Actual Qty must be ONLY what was actually completed, ";
            error = true;
        }

        //only for install jobs as pickups can be scheduled later
        if (job.typeId === 1 && job.callout && !job.reschedule && !job.onHold) {
            newErrorString += "You MUST reschedule or place this job on hold!  ";
            error = true;
        }

        var errorSimple: SimpleValidation = {
            errorMessage: newErrorString,
            error: error
        };

        return errorSimple;
    }

    save = () => {
        let items = this.state.allocatedJobs;

        //create list of items that require rescheduling
        var rescheduleJobs = items.filter(data => data.isReschedule);
        var idList: number[] = [];
        for (var i = 0; i < rescheduleJobs.length; i++) {
            //for each job, get the items that require rescheduling
            for (var y = 0; y < rescheduleJobs[i].items.length; y++) {
                let rescheduleItem = rescheduleJobs[i].items[y];
                let kwikstageItems = rescheduleItem.kwikstageItems;
                if (kwikstageItems && kwikstageItems.length > 0) {
                    //need to check the kwistage items not the normal items
                    for (var z = 0; z < kwikstageItems.length; z++) {
                        if (kwikstageItems[z].reschedule) {
                            idList.push(rescheduleItem.id);
                        }
                    }
                } else {
                    if (rescheduleItem.reschedule) {
                        //need to check if the item is set to reschedule 
                        //as can have multiple items per job where only 1 may be set to reschedule
                        idList.push(rescheduleItem.id);
                    }
                }
            }
        }

        //only save the items that have changed or ALL Items if the runsheet has been checked
        var allocated = this.state.runsheetChecked && this.state.canUpdateChecked ? items : items.filter(data => data.isDirty);
        if (allocated.length > 0) {
            this.saveRunsheet(allocated, idList);
        } else if (idList.length > 0) {
            //rescheduling whole item so don't need to save
            this.props.saveReschedule({ idList: idList });
        } else {
            toast.info("No items have been updated");
        }
    }

    async saveRunsheet(allocated: AllocatedJob[], idList: number[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var runSheet = {
            scheduleDate: this.state.scheduleDate,
            scheduleBranchId: this.state.branchFilterId,
            runsheetChecked: this.state.runsheetChecked,
            jobs: allocated,
            subId: user.sub
        }

        //use all items when save has finished
        let allItems = this.state.allocatedJobs;

        //save
        axios.post('api/RunSheet/SaveRunSheetUpdate', runSheet, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Run Sheet Update Saved");
                //reset isdirty flag
                for (var i = 0; i < allItems.length; i++) {
                    allItems[i].isDirty = false;
                }
                this.setState({ allocatedJobs: allItems, canUpdateChecked: !this.state.runsheetChecked, saveDisabled: false });
                if (idList.length > 0) {
                    this.props.saveReschedule({ idList: idList });
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveSimple = () => {
        let items = this.state.allocatedSimpleJobs;

        //create list of items that require rescheduling
        var rescheduleJobs = items.filter(data => data.reschedule);
        var idList: number[] = [];
        for (var i = 0; i < rescheduleJobs.length; i++) {
            let rescheduleItem = rescheduleJobs[i];
            if (rescheduleItem.reschedule && !rescheduleItem.isReschedule) {
                //need to check if the item is set to reschedule 
                idList.push(rescheduleItem.scheduleId);
            }
        }

        var allocatedJobs = items.filter(i => i.isDirty);
        if (allocatedJobs.length > 0) {
            this.saveRunsheetSimple(allocatedJobs, idList);
        } else if (idList.length > 0) {
            this.props.saveReschedule({ idList: idList });
        } else {
            toast.info("No items have been updated");
        } 
    }

    async saveRunsheetSimple(allocatedJobs: AllocateSimpleJob[], idList: number[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var runSheet = {
            scheduleDate: this.state.scheduleDate,
            jobs: allocatedJobs,
            subId: user.sub
        }

        //save
        axios.post('api/RunSheet/SaveRunSheetUpdateSimple', runSheet, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Run Sheet Update Saved");
                if (idList.length > 0) {
                    this.props.saveReschedule({ idList: idList });
                } else {
                    this.getSimpleRunsheetData(this.state.branchFilterId);
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    submit = (e: any) => {
        e.preventDefault();

        if(this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });

        if (this.state.systemCompanyId === 3) {
            if (this.validateSimple()) {
                this.saveSimple();
            } else {
                toast.error("Please fix validation errors on screen before Saving");
            }
        } else {
            if (this.validate()) {
                this.save();
            } else {
                if (this.state.runsheetChecked) {
                    //if validation failed while runsheet ticked we need to remove the truck filter so they can see all jobs
                    this.setState({ truckFilterId: -1, truckTeam: "" });
                }
                this.setState({ runsheetChecked: false });
                toast.error("Please fix validation errors on screen before Saving");
            } 
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();
        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let allocatedJobs = this.state.truckFilterId > 0 ? this.state.filteredJobs : this.state.allocatedJobs;
        let allocatedSimpleJobs = this.state.truckFilterId > 0 ? this.state.filteredSimpleJobs : this.state.allocatedSimpleJobs;
        let scheduleBranchOptions = this.state.scheduleBranchOptions;
        let truckOptions = this.state.truckOptions;
        let notesPopup = <NotesPopup showEditNotesModal={this.state.showNotes} editNotes={this.state.editNotes} hideNotes={this.hideNotes} updateNotes={this.updateNotes} saveNotes={this.saveNotes} />;
        let editSimpleNotes = <EditNoteModal systemCompanyId={this.state.systemCompanyId} scheduleId={this.state.scheduleId} editDriverNotes={true} showDriverScheduleNotes={true} showEditNotesModal={this.state.showSimpleNotes} save={this.saveSimpleNotes} hideModal={this.hideEditSimpleNotes} />
        let editJobPopup = <RunsheetEditJobPopup showModal={this.state.showEditJobModal} scheduleId={this.state.scheduleId} fromRunsheet={true} systemCompanyId={this.state.systemCompanyId} hideModal={this.hideEditJobModal} saveModal={this.saveEditJob} />;
        let addInvoice = <RunsheetInvoiceItemsPopup showItems={false} showModal={this.state.showInvoiceItemModal} items={this.state.items} itemTypes={this.state.itemTypes} gst={this.state.gst} invoiceItem={this.state.invoiceItem} invoiceItemModalType={this.state.invoiceItemModalType} hideModal={this.hideInvoiceModal} updateData={this.updateInvoiceItems} saveModal={this.saveInvoiceItems} />;
        let linkedJobs = <RunsheetLinkedJobPopup showModal={this.state.showLinkedJobs} linkedJobs={this.state.linkedJobs} truckOptions={this.state.truckOptions} hideModal={this.hideLinkedJobs} editJob={this.editLinkedJob} updateData={this.updateLinkedJob} saveModal={this.saveLinkedJobs} />
        let photos = <PhotoViewerModal showModal={this.state.showPhotos} photoIndex={this.state.photoIndex} photos={this.state.photos} hideModal={this.hidePhotos} update={this.updatePhotoIndex} />
        let addDetail = <RunsheetAddDetailModalData scheduleId={this.state.scheduleId} showModal={this.state.showAddUnitNumber} hideModal={this.hideAddUnitNumberSimple} save={this.saveAddUnitNumberSimple } />
        let onSiteTimes = <EditSiteTimesModal jobId={this.state.editOnSiteMins.jobId} runsheetId={this.state.editOnSiteMins.runsheetId} showEditSiteTimesModal={this.state.showOnSiteMins} hideModal={this.hideOnSiteMinutes} save={this.saveOnSiteMinutes} />

        return (
            <div>
                <div className="static-modal">
                    {notesPopup}
                    {editSimpleNotes}
                    {editJobPopup}
                    {addInvoice}
                    {linkedJobs}
                    {photos}
                    {addDetail}
                    {onSiteTimes}
                </div>
                <div className="runsheet__search--fixed">
                    <div className="runsheet__search--fixed-width">
                        <div className="runsheet__statusBar">
                            <div>
                                <label className="input-group--inline" htmlFor="scheduleDate">
                                    <span className="label">Schedule Date</span>
                                    <input type='date' id="scheduleDate" name="scheduleDate" className='input' value={this.state.scheduleDate} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <button className="defaultbutton margin10" type="button" onClick={this.refresh}>Refresh</button>
                                <label className="" htmlFor="branchFilterId">
                                    <span className="label">Branch</span>
                                    <select className="select" id="branchFilterId" name="branchFilterId" value={this.state.branchFilterId} onChange={(e) => this.handleBranchChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {scheduleBranchOptions.map(branch =>
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="truckFilterId">
                                    <span className="label">Truck</span>
                                    <select className="select" id="truckFilterId" name="truckFilterId" value={this.state.truckFilterId} onChange={(e) => this.handleTruckChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {truckOptions.map(truck =>
                                            <option key={truck.id} value={truck.id}>{truck.name}-{truck.registrationNumber}</option>
                                        )};
                                    </select>
                                </label>
                                <span>{this.state.truckTeam}</span>
                            </div>
                            <div className={this.state.unallocatedJobs > 0 ? "alignitemscentre" : "hidden"}>
                                <span className="runsheet-unallocationNote">There are {this.state.unallocatedJobs} unallocated jobs!</span>
                            </div>
                            <button className="defaultbutton margin10" type="button" onClick={this.showUpdateLinkedJobs}>Linked Jobs</button>
                        </div>
                        <hr></hr>
                    </div>
                </div>
                
                <div className={this.state.systemCompanyId === 2 ? "runsheet__topMargin" : "hidden"}>
                    <div className="runsheetTable table__margin overflowAuto">
                        <table className="runsheetTable table table__large tableColours-header">
                            <thead>
                                <tr>
                                    <th className="hidden">Id</th>
                                    <th></th>
                                    <th>Truck</th>
                                    <th>Job #</th>
                                    <th>Site Address</th>
                                    <th>Est. Time</th>
                                    <th>Depart</th>
                                    <th>Arrival</th>
                                    <th>Down Time</th>
                                    <th>Completion</th>
                                    <th>Mins</th>
                                    <th>Callout</th>
                                    <th>Invoice</th>
                                    <th>Driver Notes</th>
                                    <th></th>
                                    <th>SWMS</th>
                                </tr>
                            </thead>
                            {allocatedJobs.map((job, index) =>
                                <tbody className="runsheetRow" key={index}>
                                    <tr>
                                        <td className="hidden">{job.id}</td>
                                        <td className="table__text--align">{index + 1}</td>
                                        <td className="table__text--align"> {job.truck}</td>
                                        <td className="table__text--align"> {job.jobNumber}</td>
                                        <td className="table__text--align">
                                            <Link className={job.jobId === 0 ? "hidden" : ""} to={'/job/' + job.jobId} target="_blank">
                                                <div className="tooltipitem tooltipitem__right">{job.siteAddress ? job.siteAddress.substring(0, 20) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                                                </div>
                                            </Link>
                                            <div className={job.jobId === 0 ? "tooltipitem tooltipitem__right" : "hidden"}>{job.siteAddress ? job.siteAddress.substring(0, 20) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">
                                            {job.estTime}
                                        </td>
                                        <td className="table__text--align">
                                            <input className={job.jobNumber == 1 ? 'form-control runsheet__timeSize' : 'hidden'} type="time" value={job.departureTimeFormatted} onChange={(e) => this.onCellChange(job.itemIndex, "departureTimeFormatted", e.target.value)} disabled={this.state.runsheetChecked} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type="time" className="form-control runsheet__timeSize" value={job.siteArrivalTimeFormatted} onChange={(e) => this.onCellChange(job.itemIndex, "siteArrivalTimeFormatted", e.target.value)} disabled={this.state.runsheetChecked} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='text' pattern="(0?[0-9]|1[0-2]):[0-5][0-9]$" className="form-control runsheet__timeSize" value={job.downTime} onChange={(e) => this.onCellChange(job.itemIndex, "downTime", e.target.value)} disabled={this.state.runsheetChecked} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type="time" className="form-control runsheet__timeSize" value={job.siteCompletionTimeFormatted} onChange={(e) => this.onCellChange(job.itemIndex, "siteCompletionTimeFormatted", e.target.value)} disabled={this.state.runsheetChecked} />
                                        </td>
                                        <td>
                                            <button type="button" className={this.state.runsheetChecked ? "btn btn-link" : "hidden"} onClick={(e) => this.openOnSiteMins(e, job.itemIndex)}>{job.onSiteMins < 0 ? "Not Set" : job.onSiteMins}</button>
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className={!job.callout || (job.callout && job.calloutComplete) ? "input-control" : "hidden"} checked={job.callout} onChange={(e) => this.addCalloutInvoiceItems(e, job.itemIndex)} disabled={job.callout || this.state.runsheetChecked} />
                                            <button type="button" className={job.callout && !job.calloutComplete ? "button--round makebackgroundlightred" : "hidden"} onClick={(e) => this.invoiceItemCheck(job.itemIndex, 2)}>Callout</button>
                                        </td>
                                        <td className="table__text--align">
                                            <a className={job.jobId == 0 ? "hidden" : ""} href="#" onClick={(e) => this.addInvoiceItems(e, job.itemIndex)}>
                                                <span className="fas fa-plus copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Create Custom Invoice (USE CAUTION)</span></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align">
                                            <a className={job.driverNotes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, job.itemIndex, 0, 1)}>
                                                <div className="tooltipitem tooltipitem__left">{job.driverNotes ? job.driverNotes.substring(0, 20) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__left">{job.driverNotes}</span>
                                                </div>
                                            </a>
                                            <a className={job.driverNotes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, job.itemIndex, 0, 1)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align">
                                            <div className={job.hasPhotos ? "" : "hidden"}>
                                                <a className={job.hasPhotos ? "makeitgreen" : ""} href="#" onClick={(e) => this.viewPhotos(e, job.id)}>
                                                    <span className="fas fa-file-image edit--icon alignIconCenter"></span>
                                                </a>
                                            </div>
                                        </td>
                                        <td className="table__text--align">
                                            <a href="#" onClick={(e) => this.printSwms(e, job.itemIndex)}>
                                                <span className="fas fa-file-shield edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                    </tr>
                                    <tr className={job.error === "" ? "hidden" : ""}>
                                        <td className="makeitred" colSpan={15}>
                                            {
                                                job.error.split("\n").map((item: any, key: any) =>
                                                {
                                                    return <span className="label errors" key={key}>{item}<br /></span>
                                                })
                                            }
                                        </td>
                                    </tr>
                                    <tr className="">
                                        <td colSpan={15}>
                                            <table className='table subtable tableColours-header'>
                                                <thead>
                                                    <tr>
                                                        <th className="hidden">Id</th>
                                                        <th></th>
                                                        <th>Type</th>
                                                        <th>Item</th>
                                                        <th></th>
                                                        <th>Sched Qty</th>
                                                        <th></th>
                                                        <th>
                                                            <div className="tooltipitem tooltipitem__right">Actual Total
                                                                <span className="tooltipitemtext tooltipitemtext__right">Quantity completed during the time period</span>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="tooltipitem tooltipitem__right">Lost
                                                                <span className="tooltipitemtext tooltipitemtext__right">Any lost, stolen or damaged items, note the quantity here</span>
                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="tooltipitem tooltipitem__right">Re-Schedule
                                                                <span className="tooltipitemtext tooltipitemtext__right">Tick the box if the job needs to roll over</span>
                                                            </div>
                                                        </th>
                                                        <th>Edit</th>
                                                        <th>Notes</th>
                                                    </tr>
                                                </thead>
                                                {job.items.map((item, index) =>
                                                    <React.Fragment key={index}>
                                                        <tbody className={item.kwikstageItems.length === 0 ? "" : "hidden"}>
                                                            <RunsheetRow index={index} job={job} item={item} runsheetChecked={this.state.runsheetChecked} editNotes={this.editNotes} editJob={this.editJob} onSubgridCellChange={this.onSubgridCellChange} />
                                                        </tbody>
                                                        <tbody className={item.kwikstageItems.length === 0 ? "hidden" : ""}>
                                                            {item.kwikstageItems.map((ksItem, ksIndex) =>
                                                                <RunsheetKwikstageRow key={ksItem.scheduleKwikstageId} ksIndex={ksIndex} runsheetChecked={this.state.runsheetChecked} job={job} item={item} ksItem={ksItem} editNotes={this.editNotes} editJob={this.editJob} onKwikstageSubgridCellChange={this.onKwikstageSubgridCellChange} onKwikstageSubgridCheckChange={this.onKwikstageSubgridCheckChange} />
                                                            )}
                                                        </tbody>
                                                    </React.Fragment>
                                                
                                                )}
                                            </table>

                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                        {this.state.errors["allocatedItems"] ?
                            (this.state.errors["allocatedItems"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                    </div>
                </div>
                <div className={this.state.systemCompanyId === 3 ? "runsheet__topMargin" : "hidden"}>
                    <div className="marginLeft10 noteSize">* Items in red are pending go-ahead sign-off.</div>
                    <div className="runsheetTable table__margin overflowAuto">
                        <table className="runsheetTable table table__large tableColours-header">
                            <thead>
                                <tr>
                                    <th className="hidden">Id</th>
                                    <th></th>
                                    <th>Truck</th>
                                    <th>Job #</th>
                                    <th>Customer</th>
                                    <th>Dash Job Id</th>
                                    <th>Site Address</th>
                                    <th>Item</th>
                                    <th>Unit #</th>
                                    <th>Qty</th>
                                    <th>
                                        <input className={this.state.canEdit && this.state.truckFilterId > 0 ? "marginRight10" : "hidden"} type='checkbox' id="updateAll" name="updateAll" checked={this.state.updateAll} onChange={(e) => this.updateJobQuantitySimple()} />
                                        <div className="tooltipitem tooltipitem__right">Actual Qty
                                            <span className="tooltipitemtext tooltipitemtext__right">Quantity completed during the time period</span>
                                        </div>
                                    </th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Lost Qty
                                            <span className="tooltipitemtext tooltipitemtext__right">Any lost, stolen or damaged items, note the quantity here</span>
                                        </div>
                                    </th>
                                    <th>Callout</th>
                                    <th>Reschedule</th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Hold
                                            <span className="tooltipitemtext tooltipitemtext__right">Place Job on Hold to schedule later</span>
                                        </div>
                                    </th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Invoice P&C
                                            <span className="tooltipitemtext tooltipitemtext__right">Invoice less P&C than scheduled</span>
                                        </div>
                                    </th>
                                    <th>Edit Job</th>
                                    <th>Invoice Extra</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            {allocatedSimpleJobs.map((job, index) =>
                                <tbody className="runsheetRow" key={index}>
                                    <tr className={(job.itemTypeId === 26 || job.itemTypeId === 36) && job.frequencyId === 1 ? "tableColours--pc--monthly" : (job.itemTypeId === 26 || job.itemTypeId === 36) && job.frequencyId === 2 ? "tableColours--pc--fortnightly" : (job.itemTypeId === 26 || job.itemTypeId === 36) && job.frequencyId === 3 ? "tableColours--pc--weekly" : (job.itemTypeId === 26 || job.itemTypeId === 36) ? "tableColours--pc--oneoff" : job.typeId === 1 && job.isPlaceholder ? "tableColours--install-ph" : job.manualItem && this.state.systemCompanyId === 3 ? "tableColours--manual" : job.typeId === 3 ? "tableColours--assist" : job.typeId === 4 ? "tableColours--cleaning" : job.typeId === 5 ? "tableColours--manual" : job.typeId === 1 && job.isCustomerPickup ? "tableColours--cust--install" : job.typeId === 1 ? "tableColours--install" : job.typeId === 2 && job.isPlaceholder ? "tableColours--pickup-ph" : job.typeId === 2 && job.isCustomerPickup ? "tableColours--cust--pickup" : job.typeId === 2 ? "tableColours--pickup" : ""}>
                                        <td className="hidden">{job.scheduleId}</td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}>{index + 1}</td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}> {job.truck}</td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}> {job.jobNumber}</td>
                                        <td className="table__text--align">{job.customer}</td>
                                        <td className="table__text--align">
                                            <Link className={job.jobId === 0 ? "hidden" : job.goAhead ? "" : "allocate__redText"} to={'/job/' + job.jobId} target="_blank">
                                                {job.jobId}
                                            </Link>
                                        </td>
                                        <td className="table__text--align">
                                            <div className={job.jobId === 0 ? "hidden" : job.goAhead ? "" : "allocate__redText"}>
                                                <div className="tooltipitem tooltipitem__right">{job.siteAddress ? job.siteAddress.substring(0, 30) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                                                </div>
                                            </div>
                                            <div className={job.jobId === 0 ? "tooltipitem tooltipitem__right" : "hidden"}>{job.siteAddress ? job.siteAddress.substring(0, 30) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                                            </div>
                                        </td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}>
                                            <div className="tooltipitem tooltipitem__right">{job.manualItem || (job.callout && job.description) ? job.description.substring(0, 10) + "... " : job.itemShortcut}
                                                <span className="tooltipitemtext tooltipitemtext__right">{job.description != "" ? job.description : job.item}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">
                                            <a href="#" className={job.showUnitNumber ? "alignIconCenter tooltipitem tooltipitem__right" : "hidden"} onClick={(e) => this.addUnitNumberSimple(e, job.itemIndex)}>
                                                <span className={"fa-solid fa-circle-exclamation copy--icon" + (job.itemUnitNumber === "" && job.unitNumberRequired ? " makeitred" : "")}></span>
                                                <span className="tooltipitemtext tooltipitemtext__right">Unit Number is Required. Click on icon to add.</span>
                                            </a>
                                        </td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}>
                                            {job.quantityScheduled}
                                        </td>
                                        <td className={"table__text--align" + (job.unitNumberRequired && job.itemUnitNumber === "" ? " makeitredbackground" : "")}>
                                            <input type='number' min={0} step={1} className={"form-control allocate__qtySize" + (job.goAhead ? "" : " allocate__redText")} id="quantity" name="quantity" value={job.quantity} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || job.reschedule || !job.goAhead || (job.unitNumberRequired && job.itemUnitNumber === "" ) || job.itemTypeId === 50 || job.itemTypeId === 54} />
                                        </td>
                                        <td className={"table__text--align" + (job.goAhead ? "" : " allocate__redText")}>
                                            <input type='number' min={0} step={1} className={job.typeId === 1 ? "hidden" : "form-control allocate__qtySize" + (job.goAhead ? "" : " allocate__redText")} id="quantityLost" name="quantityLost" value={job.quantityLost} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || (job.unitNumberRequired && job.itemUnitNumber === "")} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className="input-control" id="callout" name="callout" checked={job.callout} onChange={(e) => this.addCalloutSimpleInvoiceItems(e, job.itemIndex)} disabled={job.quantity > 0 || !this.state.canEdit || !job.canEdit || job.calloutItem || !job.goAhead } />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className="input-control" id="reschedule" name="reschedule" checked={job.reschedule} onChange={(e) => this.onCellCheckboxSimpleRescheduleChange(e, job.itemIndex)} disabled={job.manualItem || !this.state.canEdit || !job.canEdit || job.calloutItem || !job.goAhead} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className="input-control" id="onHold" name="onHold" checked={job.onHold} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={job.manualItem || job.typeId === 2 || !this.state.canEdit || !job.canEdit || !job.callout || !job.goAhead} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className={job.itemTypeId === 26 && job.frequencyId > 0 && job.quantityScheduled > 1 ? "input-control" : "hidden"} id="invoice" name="invoice" checked={job.invoice} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={job.manualItem || job.typeId === 2 || !this.state.canEdit || !job.canEdit || job.callout || !job.goAhead} />
                                        </td>
                                        <td className="table__text--align">
                                            <a className={this.state.canEdit && job.canEditJob && job.itemTypeId != 50 && job.itemTypeId != 51 && job.itemTypeId != 61 ? "" : "hidden"} href="#" onClick={(e) => this.editSimpleJob(e, job.itemIndex)}>
                                                <span className="fas fa-folder-open copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Edit job if changes to plan (USE CAUTION)</span></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align">
                                            <a className={job.jobId == 0 || !this.state.canEdit ? "hidden" : ""} href="#" onClick={(e) => this.addInvoiceItemsSimple(e, job.itemIndex)}>
                                                <span className="fas fa-plus copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Create Custom Invoice (USE CAUTION)</span></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align">
                                            <a className={job.scheduleNotes == "" ? "hidden" : job.goAhead ? "" : " allocate__redText"} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editSimpleNotes(e, job.itemIndex)}>
                                                <div className="tooltipitem tooltipitem__left">{job.scheduleNotes ? job.scheduleNotes.substring(0, 20) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__left">{job.scheduleNotes}</span>
                                                </div>
                                            </a>
                                            <a className={job.scheduleNotes != "" ? "hidden" : job.goAhead ? "" : " allocate__redText"} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editSimpleNotes(e, job.itemIndex)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                    </tr>

                                    <tr className={job.productGroupId === 6 ? "" : "hidden"}>
                                        <td colSpan={18}>
                                            <div className={job.itemTypeId === 61 ? "hidden" : "makeitflexcenter alignitemscentre"}>
                                                Total Litres <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="size" name="size" value={job.size} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule} />
                                                Extra Hours Onsite <input type='number' min={0} step={0.5} className="allocate__qtySize marginLeft10 marginRight10" id="extraHours" name="extraHours" value={job.extraHours} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule} />
                                                <span className={job.itemTypeId === 59 ? "" : "hidden"}>Water Board Notified?</span>
                                                <input type='checkbox' className={job.itemTypeId === 59 ? "input-control marginLeft10" : "hidden"} id="waterBoardNotified" name="waterBoardNotified" checked={job.waterBoardNotified} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule}></input>
                                            </div>
                                            <div className={job.itemTypeId === 61 ? "makeitflexcenter alignitemscentre" : "hidden"}>
                                                Total Litres <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="size" name="size" value={job.size} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule} />
                                                Truck Hours <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="extraHours" name="extraHours" value={job.extraHours} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule} />
                                                <span>Confined Space?</span>
                                                <input type='checkbox' className="input-control marginLeft10 marginRight10" id="confinedSpace" name="confinedSpace" checked={job.confinedSpace} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule}></input>
                                                <span className={job.epaTransportCertificateCheckRequired ? "" : "hidden"}>Epa Transport Certificate Litres matches Total Litres entered?</span>
                                                <input type='checkbox' className={job.epaTransportCertificateCheckRequired ? "input-control marginLeft10" : "hidden"} id="epaTransportCertificateChecked" name="epaTransportCertificateChecked" checked={job.epaTransportCertificateChecked} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout || job.reschedule}></input>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr className={(job.itemTypeId === 50 || job.itemTypeId === 56) ? "" : "hidden"}>
                                        <td colSpan={18}>
                                            <div className="makeitflexcenter alignitemscentre">
                                                Panels <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanels" name="fencePanels" value={job.fencePanels} onChange={(e) => this.onCellSimplePanelChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Blocks <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBlocks" name="fenceBlocks" value={job.fenceBlocks} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Clamps <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceClamps" name="fenceClamps" value={job.fenceClamps} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Pickets <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePickets" name="fencePickets" value={job.fencePickets} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Signs <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceSigns" name="fenceSigns" value={job.fenceSigns} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                <span className={job.hasBraceItem ? "hidden" : ""}>Braces</span> <input type='number' min={0} step={1} className={job.hasBraceItem ? "hidden" : "allocate__qtySize marginLeft10 marginRight10"} id="fenceBraces" name="fenceBraces" value={job.fenceBraces} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                <span className={job.typeId != 2 ? "hidden" : ""}>Damaged</span> <input type='number' min={0} step={1} className={job.typeId != 2 ? "hidden" : "allocate__qtySize marginLeft10 marginRight10"} id="fencePanelsDamaged" name="fencePanelsDamaged" value={job.fencePanelsDamaged} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                <span className={this.state.canEdit && job.canEdit ? "" : "hidden"}>{job.typeId === 2 && job.itemTypeId === 50 ? "Create Part Pickup Invoice?" : "Ready for Invoice?"}</span>
                                                <input type='checkbox' className={this.state.canEdit && job.canEdit ? "input-control marginLeft10" : "hidden"} id="invoice" name="invoice" checked={job.invoice} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={(job.quantity + job.quantityLost) === job.quantityScheduled || job.callout} />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className={job.itemTypeId === 54 ? "" : "hidden"}>
                                        <td colSpan={13}>
                                            <div className="alignitemsright-centretext">
                                                On Site Panels <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanels" name="fencePanels" value={job.fencePanels} onChange={(e) => this.onCellSimplePanelChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Blocks <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBlocks" name="fenceBlocks" value={job.fenceBlocks} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Clamps <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceClamps" name="fenceClamps" value={job.fenceClamps} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Pickets <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePickets" name="fencePickets" value={job.fencePickets} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Signs <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceSigns" name="fenceSigns" value={job.fenceSigns} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Braces<input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBraces" name="fenceBraces" value={job.fenceBraces} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Damaged<input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanelsDamaged" name="fencePanelsDamaged" value={job.fencePanelsDamaged} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                            </div>
                                        </td>
                                        <td colSpan={5} rowSpan={2} className="alignitemsverticalcentre">
                                            <span className={this.state.canEdit && job.canEdit ? "marginLeft10" : "hidden"}>Ready for Invoice?</span>
                                            <input type='checkbox' className={this.state.canEdit && job.canEdit ? "input-control marginLeft10" : "hidden"} id="invoice" name="invoice" checked={job.invoice} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={(job.quantity + job.quantityLost) === job.quantityScheduled || job.callout} />
                                        </td>
                                    </tr>
                                    <tr className={job.itemTypeId === 54 ? "" : "hidden"}>
                                        <td colSpan={13}>
                                            <div className="alignitemsright-centretext">
                                                <span className="runsheet-extaPanel--padding">Extra Panels</span> <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanelsExtra" name="fencePanelsExtra" value={job.fencePanelsExtra} onChange={(e) => this.onCellSimplePanelChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Blocks <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBlocksExtra" name="fenceBlocksExtra" value={job.fenceBlocksExtra} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Clamps <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceClampsExtra" name="fenceClampsExtra" value={job.fenceClampsExtra} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Pickets <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePicketsExtra" name="fencePicketsExtra" value={job.fencePicketsExtra} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Signs <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceSignsExtra" name="fenceSignsExtra" value={job.fenceSignsExtra} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Braces <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBracesExtra" name="fenceBracesExtra" value={job.fenceBracesExtra} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Damaged<input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanelsDamagedExtra" name="fencePanelsDamagedExtra" value={0} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={true} />
                                            </div>
                                        </td>

                                    </tr>
                                    <tr className={job.itemTypeId === 51 ? "" : "hidden"}>
                                        <td colSpan={18}>
                                            <div className="makeitflexcenter alignitemscentre">
                                                Blocks <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBlocks" name="fenceBlocks" value={job.fenceBlocks} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Clamps <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceClamps" name="fenceClamps" value={job.fenceClamps} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                <span className={this.state.canEdit && job.canEdit ? "" : "hidden"}>Ready for Invoice?</span>
                                                <input type='checkbox' className={this.state.canEdit && job.canEdit ? "input-control marginLeft10" : "hidden"} id="invoice" name="invoice" checked={job.invoice} onChange={(e) => this.onCellCheckboxSimpleChange(e, job.itemIndex)} disabled={(job.quantity + job.quantityLost) === job.quantityScheduled || job.callout} />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className={((job.itemTypeId === 22 && job.itemTypeGroupId === 4) || job.itemTypeId === 52) && job.jobId > 0 ? "" : "hidden"}>
                                        <td colSpan={18}>
                                            <div className="makeitflexcenter alignitemscentre">
                                                Panels <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePanels" name="fencePanels" value={job.fencePanels} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Blocks <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBlocks" name="fenceBlocks" value={job.fenceBlocks} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Clamps <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceClamps" name="fenceClamps" value={job.fenceClamps} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Pickets <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fencePickets" name="fencePickets" value={job.fencePickets} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Signs <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceSigns" name="fenceSigns" value={job.fenceSigns} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                                Braces <input type='number' min={0} step={1} className="allocate__qtySize marginLeft10 marginRight10" id="fenceBraces" name="fenceBraces" value={job.fenceBraces} onChange={(e) => this.onCellSimpleChange(e, job.itemIndex)} disabled={!this.state.canEdit || !job.canEdit || !job.goAhead || job.callout} />
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                        {this.state.errors["allocatedItems"] ?
                            (this.state.errors["allocatedItems"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                        {this.state.errors["allocatedItemsOther"] ?
                            (this.state.errors["allocatedItemsOther"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            }) : ""
                        }
                    </div>
                </div>
		        <div className="marginBottom10">
                    <div className={this.state.unallocatedJobs > 0 ? "colAlignRight" : "hidden"}>
                        <span className="runsheet-unallocationNote">There are {this.state.unallocatedJobs} unallocated jobs! </span><span className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Allocate/Move jobs before sending invoices.</span>
                    </div>
                    <label className={this.state.systemCompanyId === 3 ? "hidden" : "colAlignRight"} htmlFor="runsheetChecked">
                        <span className="label-large marginRight10">Runsheet Times Correct - Send Invoices</span>
                        <input className="checkbox" type="checkbox" id="runsheetChecked" name="runsheetChecked" checked={this.state.runsheetChecked} onChange={(e) => this.handleCheckboxChange(e)}></input>
                    </label>
                </div>
                <div className="colAlignRight marginBottom10">
                    <button className="defaultbutton" type="button" onClick={this.submit} disabled={this.state.saveDisabled}>Save</button>
                </div>
            </div>
        );

    }

};

const RunsheetRow = ({ index, job, item, runsheetChecked, editNotes, editJob, onSubgridCellChange }: { index: number, job: AllocatedJob, item: AllocatedJobItem, runsheetChecked: boolean, editNotes(e: any, jobIndex: number, itemIndex: number, noteType: number): void, editJob(e: any, jobIndex: number, itemIndex: number): void, onSubgridCellChange(jobIndex: number, itemIndex: number, item: string, value: any): void }) => {
    return (
        <tr key={index} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : item.typeId === 4 ? "tableColours--cleaning" : "tableColours--manual"}>
            <td className="hidden">{item.id}</td>
            <td className="table__text--align">{index + 1}</td>
            <td className="table__text--align">{item.type}</td>
            <td className="table__text--align">
                <div className="tooltipitem tooltipitem__right">{item.manualItem || (item.calloutItem && item.description != "") ? item.description.substring(0, 30) + "... " : item.item}
                    <span className="tooltipitemtext tooltipitemtext__right">{item.description}</span>
                </div>
            </td>
            <td className="table__text--align makeitred makeitbold">{item.manualItem ? "MANUAL JOB" : ""}</td>
            <td className="table__text--align">{item.quantityScheduled}</td>
            <td className="table__text--align">{item.unitOfMeasure}</td>
            <td className="table__text--align">
                <input type='number' min={0} step={1} className="form-control allocate__qtySize" value={item.quantity} onChange={(e) => onSubgridCellChange(job.itemIndex, item.itemIndex, "quantity", e.target.value)} disabled={runsheetChecked || (item.reschedule && item.rescheduleComplete)} />
            </td>
            <td className="table__text--align">
                <input type='number' min={0} step={1} className={item.typeId === 1 ? "hidden" : "form-control allocate__qtySize"} value={item.quantityLost} onChange={(e) => onSubgridCellChange(job.itemIndex, item.itemIndex, "quantityLost", e.target.value)} disabled={runsheetChecked} />
            </td>
            <td className="table__text--align">
                <input type='checkbox' className="input-control" checked={item.reschedule} onChange={(e) => onSubgridCellChange(job.itemIndex, item.itemIndex, "reschedule", e.target.checked)} disabled={item.manualItem || runsheetChecked} />
            </td>
            <td className="table__text--align">
                <a className={item.type == "Install" && !item.manualItem && !runsheetChecked && !item.invoiceItem ? "" : "hidden"} href="#" onClick={(e) => editJob(e, job.itemIndex, item.itemIndex)}>
                    <span className="fas fa-folder-open copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Edit job if changes to plan (USE CAUTION)</span></span>
                </a>
            </td>
            <td className="table__text--align">
                <a className={item.scheduleNotes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, job.itemIndex, item.itemIndex, 2)}>
                    <div className="tooltipitem tooltipitem__left">{item.scheduleNotes ? item.scheduleNotes.substring(0, 20) + "... " : ""}
                        <span className="tooltipitemtext tooltipitemtext__left">{item.scheduleNotes}</span>
                    </div>
                </a>
                <a className={item.scheduleNotes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, job.itemIndex, item.itemIndex, 2)}>
                    <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                </a>
            </td>
        </tr>
    )
};

const RunsheetKwikstageRow = ({ ksIndex, job, item, ksItem, runsheetChecked, editNotes, editJob, onKwikstageSubgridCellChange, onKwikstageSubgridCheckChange }: { ksIndex: number, job: AllocatedJob, item: AllocatedJobItem, ksItem: AllocateJobKwikstageItem, runsheetChecked: boolean, editNotes(e: any, jobIndex: number, itemIndex: number, noteType: number): void, editJob(e: any, jobIndex: number, itemIndex: number): void, onKwikstageSubgridCellChange(e: any, jobIndex: number, itemIndex: number, kwikstageIndex: number): void, onKwikstageSubgridCheckChange(e: any, jobIndex: number, itemIndex: number, kwikstateIndex: number): void }) => {
    return (
        <tr key={ksItem.scheduleKwikstageId} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : item.typeId === 4 ? "tableColours--cleaning" : "tableColours--manual"}>
            <td className="hidden">{item.id}</td>
            <td className="table__text--align">{ksIndex + 1}</td>
            <td className="table__text--align">{item.type}</td>
            <td className="table__text--align">
                <div className="tooltipitem tooltipitem__right">{item.manualItem || (item.calloutItem && item.description != "") ? item.description.substring(0, 30) + "... " : item.item}
                    <span className="tooltipitemtext tooltipitemtext__right">{item.description}</span>
                </div>
            </td>
            <td className="table__text--align makeitred makeitbold">{item.manualItem ? "MANUAL JOB" : ""}</td>
            <td className="table__text--align">{ksItem.bays > 0 ? ksItem.bays : item.quantityScheduled}</td>
            <td className="table__text--align">{ksItem.bays > 0 ? ksItem.starterBay + " - " + ksItem.lift + " bays" : item.unitOfMeasure}</td>
            <td className="table__text--align">
                <input type='number' min={0} step={1} className="form-control allocate__qtySize" name="baysComplete" id="baysComplete" value={ksItem.baysComplete} onChange={(e) => onKwikstageSubgridCellChange(e, job.itemIndex, item.itemIndex, ksIndex)} disabled={runsheetChecked} />
            </td>
            <td className="table__text--align">
                <input type='number' min={0} step={1} className={item.typeId === 1 ? "hidden" : "form-control allocate__qtySize"} name="baysLost" id="baysLost" value={ksItem.baysLost} onChange={(e) => onKwikstageSubgridCellChange(e, job.itemIndex, item.itemIndex, ksIndex)} disabled={runsheetChecked} />
            </td>
            <td className="table__text--align">
                <input type='checkbox' className="input-control" name="reschedule" id="reschedule" checked={ksItem.reschedule} onChange={(e) => onKwikstageSubgridCheckChange(e, job.itemIndex, item.itemIndex, ksIndex)} disabled={item.manualItem || runsheetChecked} />
            </td>
            <td className="table__text--align">
                <a className={item.type == "Install" && !item.manualItem && !runsheetChecked && !item.invoiceItem ? "" : "hidden"} href="#" onClick={(e) => editJob(e, job.itemIndex, item.itemIndex)}>
                    <span className="fas fa-folder-open copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Edit job if changes to plan (USE CAUTION)</span></span>
                </a>
            </td>
            <td className="table__text--align">
                <a className={item.scheduleNotes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, job.itemIndex, item.itemIndex, 2)}>
                    <div className="tooltipitem tooltipitem__left">{item.scheduleNotes ? item.scheduleNotes.substring(0, 20) + "... " : ""}
                        <span className="tooltipitemtext tooltipitemtext__left">{item.scheduleNotes}</span>
                    </div>
                </a>
                <a className={item.scheduleNotes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, job.itemIndex, item.itemIndex, 2)}>
                    <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                </a>
            </td>
        </tr>
    )
};